import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, Cell, ResponsiveContainer } from 'recharts';

import {
  BarChart3,
  BarChartHorizontal,
  Boxes,
  Briefcase,
  Building2,
  Calendar,
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  FileSpreadsheet,
  Activity,
  Layers3,
  LayoutDashboard,
  LayoutList,
  ListFilter,
  MapPin,
  Menu,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  TrendingUp,
  Users,
  X
} from 'lucide-react';
import * as XLSX from 'xlsx';

const FILTER_HIERARCHY = ['company', 'sede', 'department', 'area', 'section', 'occupation'];

const FILTER_OPTIONS = [
  { key: null, label: 'Ninguno', icon: X },
  { key: 'company', label: 'Empresa', icon: Building2 },
  { key: 'sede', label: 'Sede', icon: MapPin },
  { key: 'department', label: 'Departamento', icon: Network },
  { key: 'area', label: 'Área', icon: Layers3 },
  { key: 'section', label: 'Sección', icon: Boxes },
  { key: 'occupation', label: 'Ocupación', icon: Briefcase }
];

const ANALYTICS_COLORS = ['#FFBB28', '#0088FE', '#00C49F', '#FF8042', '#8884D8', '#82CA9D', '#FF6666', '#A28CFF', '#FFB347', '#B6FFB3'];

const normalizeSelectedFilters = (value) => {
  const filters = Array.isArray(value) ? value : (value ? [value] : []);
  return [...new Set(filters)]
    .filter(key => FILTER_HIERARCHY.includes(key))
    .sort((a, b) => FILTER_HIERARCHY.indexOf(a) - FILTER_HIERARCHY.indexOf(b))
    .slice(0, 2);
};

const getQuestionAnchorId = (questionKey) => `analytics-question-${String(questionKey).replace(/[^a-zA-Z0-9_-]+/g, '-')}`;
const getNavigationQuestionTitle = (title) => String(title || '').replace(/^\s*\d+\s*[.)-]\s*/, '').trim();

const QuestionFilterControls = ({ selectedFilters, onToggle }) => (
  <div className="analytics-filter-tabs">
    {FILTER_OPTIONS.map(opt => {
      const active = opt.key === null ? selectedFilters.length === 0 : selectedFilters.includes(opt.key);
      const disabled = opt.key !== null && selectedFilters.length === 2 && !active;
      const Icon = opt.icon;
      return (
        <button
          key={opt.key || 'none'}
          type="button"
          aria-pressed={active}
          disabled={disabled}
          title={disabled ? 'Desmarca un filtro para seleccionar otro.' : undefined}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            background: active ? '#eef6ff' : '#ffffff',
            border: active ? '1px solid #60a5fa' : '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            fontWeight: active ? 700 : 500,
            fontSize: 14,
            color: active ? '#0b5ed7' : '#111827',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.45 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7
          }}
          onClick={() => onToggle(opt.key)}
        >
          <Icon size={15} strokeWidth={1.8} aria-hidden="true" />
          {opt.label}
        </button>
      );
    })}
    {selectedFilters.length > 0 && (
      <span style={{ marginLeft: 8, color: '#64748b', fontSize: 13, fontWeight: 600 }}>
        {selectedFilters.length} de 2 filtros activos
      </span>
    )}
  </div>
);

const HierarchyValueSelector = ({ idPrefix, label, values, excludedValues, onApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [draftExcludedValues, setDraftExcludedValues] = useState(excludedValues);
  const selectorRef = useRef(null);
  const searchInputRef = useRef(null);
  const popupId = `hierarchy-popup-${idPrefix}`.replace(/[^a-zA-Z0-9_-]+/g, '-').toLowerCase();
  const visibleCount = values.filter(value => !excludedValues.includes(value)).length;
  const allVisible = visibleCount === values.length;
  const draftVisibleCount = values.filter(value => !draftExcludedValues.includes(value)).length;
  const allDraftVisible = values.length > 0 && draftVisibleCount === values.length;
  const normalizedSearch = searchTerm.trim().toLocaleLowerCase('es');
  const filteredValues = values.filter(value =>
    !normalizedSearch || String(value).toLocaleLowerCase('es').includes(normalizedSearch)
  );
  const selectedValue = visibleCount === 1
    ? values.find(value => !excludedValues.includes(value))
    : null;
  const selectionSummary = allVisible
    ? `Todos (${values.length})`
    : visibleCount === 0
      ? 'Sin selección'
      : visibleCount === 1
        ? selectedValue
        : `${visibleCount} seleccionados`;

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!selectorRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick, true);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick, true);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const focusFrame = window.requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [isOpen]);

  const toggleSelector = () => {
    if (!isOpen) {
      setDraftExcludedValues(excludedValues.filter(value => values.includes(value)));
      setSearchTerm('');
    }
    setIsOpen(current => !current);
  };

  const toggleDraftValue = (value) => {
    setDraftExcludedValues(current => {
      if (current.length === 0) {
        return values.filter(item => item !== value);
      }

      return current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
    });
  };

  const applySelection = () => {
    onApply(draftExcludedValues);
    setIsOpen(false);
  };

  return (
    <div ref={selectorRef} className={`analytics-value-selector ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className="analytics-value-selector-trigger"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-controls={popupId}
        onClick={toggleSelector}
      >
        <ListFilter size={16} strokeWidth={1.8} aria-hidden="true" />
        <span className="analytics-value-selector-trigger-copy">
          <span className="analytics-value-selector-label">{label}</span>
          <span className="analytics-value-selector-summary">{selectionSummary}</span>
        </span>
        <ChevronDown size={16} className="analytics-value-selector-chevron" aria-hidden="true" />
      </button>

        <div id={popupId} className="analytics-value-selector-popover" role="dialog" aria-hidden={!isOpen} aria-label={`Filtrar por ${label}`}>
          <label className="analytics-value-selector-search">
            <Search size={16} aria-hidden="true" />
            <input
              ref={searchInputRef}
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={`Buscar ${label.toLowerCase()}`}
            />
          </label>

          <label className={`analytics-value-selector-option analytics-value-selector-all ${allDraftVisible ? 'is-selected' : ''}`}>
            <input
              type="checkbox"
              checked={allDraftVisible}
              onChange={() => setDraftExcludedValues(allDraftVisible ? [...values] : [])}
            />
            <span>Todas las opciones</span>
          </label>

          <div className="analytics-value-selector-options" role="group" aria-label={`Opciones de ${label}`}>
            {filteredValues.map((value, index) => {
              const checked = !allDraftVisible && !draftExcludedValues.includes(value);
              const inputId = `hierarchy-${idPrefix}-${index}-${String(value)}`.replace(/[^a-zA-Z0-9_-]+/g, '-').toLowerCase();
              return (
                <label key={value} htmlFor={inputId} className={`analytics-value-selector-option ${checked ? 'is-selected' : ''}`}>
                  <input id={inputId} type="checkbox" checked={checked} onChange={() => toggleDraftValue(value)} />
                  <span>{value}</span>
                </label>
              );
            })}
            {filteredValues.length === 0 && (
              <div className="analytics-value-selector-empty">No se encontraron opciones.</div>
            )}
          </div>

          <div className="analytics-value-selector-footer">
            <span>{allDraftVisible ? 'Todas las opciones' : `${draftVisibleCount} ${draftVisibleCount === 1 ? 'seleccionada' : 'seleccionadas'}`}</span>
            <div>
              <button type="button" className="analytics-value-selector-clear" onClick={() => setDraftExcludedValues([])}>Limpiar</button>
              <button type="button" className="analytics-value-selector-apply" onClick={applySelection}>Aplicar</button>
            </div>
          </div>
        </div>
    </div>
  );
};

const HorizontalHierarchyChart = ({ hierarchy, categories, colors, parentLabel, childLabel, collapsedParents, onToggleParent }) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const chartRef = useRef(null);
  const moveTooltip = (event, segment) => {
    const bounds = chartRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const tooltipWidth = 270;
    const tooltipHeight = 100;
    const x = Math.min(Math.max(event.clientX - bounds.left + 14, 8), Math.max(8, bounds.width - tooltipWidth - 8));
    const y = Math.min(Math.max(event.clientY - bounds.top + 14, 8), Math.max(8, bounds.height - tooltipHeight - 8));
    setHoveredSegment({ ...segment, x, y });
  };

  return (
    <div
      ref={chartRef}
      className="analytics-horizontal-hierarchy-chart"
      onMouseLeave={() => setHoveredSegment(null)}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(210px, 30%) 1fr 72px', gap: 12, alignItems: 'end', marginBottom: 8 }}>
        <strong style={{ color: '#334155', fontSize: 13 }}>{parentLabel} / {childLabel}</strong>
        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: 12 }}>
          <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
        </div>
        <span />
      </div>

      {hierarchy.map(parent => {
        const isCollapsed = collapsedParents.has(parent.name);
        return (
        <div key={parent.name} style={{ marginTop: 10 }}>
          <button
            type="button"
            aria-expanded={!isCollapsed}
            onClick={() => onToggleParent(parent.name)}
            className="analytics-hierarchy-parent-row"
          >
            <strong className="analytics-hierarchy-parent-label">
              <ChevronDown className={`analytics-hierarchy-chevron ${isCollapsed ? 'is-collapsed' : ''}`} size={16} strokeWidth={2.2} aria-hidden="true" />
              <span>{parent.name}</span>
            </strong>
            <div style={{ height: 1, background: '#dbeafe' }} />
            <span className="analytics-hierarchy-total">n = {parent.base}</span>
          </button>

          <div className={`analytics-hierarchy-children ${isCollapsed ? 'is-collapsed' : ''}`} aria-hidden={isCollapsed}>
            <div className="analytics-hierarchy-children-inner">
              {parent.children.map(child => {
                const seriesTotal = categories.reduce((sum, category) => sum + (child.counts[category] || 0), 0);
                return (
                <div key={`${parent.name}-${child.name}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(210px, 30%) 1fr 72px', gap: 12, alignItems: 'center', padding: '7px 10px 7px 34px' }}>
                <span title={child.name} style={{ color: '#334155', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{child.name}</span>
                <div style={{ height: 28, borderRadius: 6, background: 'repeating-linear-gradient(to right, #f1f5f9 0, #f1f5f9 calc(25% - 1px), #dbe3ee 25%)', boxShadow: 'inset 0 0 0 1px #e2e8f0' }}>
                  <div style={{ display: 'flex', width: '100%', height: '100%', borderRadius: 6, overflow: 'hidden' }}>
                    {categories.map((category, index) => {
                      const count = child.counts[category] || 0;
                      const percentage = seriesTotal > 0 ? (count / seriesTotal) * 100 : 0;
                      if (count === 0) return null;
                      return (
                        <div
                          key={`${parent.name}-${child.name}-${category}`}
                          role="img"
                            tabIndex={isCollapsed ? -1 : 0}
                          aria-label={`${category}: ${count}, ${percentage.toFixed(1)}%`}
                          onMouseEnter={(event) => moveTooltip(event, { parent, child, category, count, percentage, color: colors[index % colors.length] })}
                          onMouseMove={(event) => moveTooltip(event, { parent, child, category, count, percentage, color: colors[index % colors.length] })}
                          onMouseLeave={() => setHoveredSegment(null)}
                          onFocus={(event) => {
                            const bounds = event.currentTarget.getBoundingClientRect();
                            moveTooltip({ clientX: bounds.right, clientY: bounds.top }, { parent, child, category, count, percentage, color: colors[index % colors.length] });
                          }}
                          onBlur={() => setHoveredSegment(null)}
                          style={{
                            width: `${percentage}%`,
                            minWidth: percentage > 0 ? 3 : 0,
                            background: category === 'Sin responder' ? '#e0e0e0' : colors[index % colors.length],
                            borderRight: '1px solid rgba(255,255,255,0.8)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'default'
                          }}
                        >
                          {percentage >= 16
                            ? `${percentage.toFixed(0)}% · ${count}`
                            : percentage >= 9
                              ? `${percentage.toFixed(0)}%`
                              : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <span className="analytics-hierarchy-total">n = {child.base}</span>
              </div>
                );
              })}
            </div>
          </div>
        </div>
        );
      })}

      {hoveredSegment && (
        <div style={{ position: 'absolute', zIndex: 5, left: hoveredSegment.x, top: hoveredSegment.y, width: 270, boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #dbe3ee', borderRadius: 9, background: '#fff', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontSize: 13, pointerEvents: 'none' }}>
          <div style={{ fontWeight: 800, marginBottom: 7 }}>{hoveredSegment.parent.name} · {hoveredSegment.child.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: hoveredSegment.category === 'Sin responder' ? '#e0e0e0' : hoveredSegment.color }} />
            <strong>Opción: {hoveredSegment.category}</strong>
          </div>
          <div style={{ marginTop: 5, color: '#64748b' }}>{hoveredSegment.count} respuestas · {hoveredSegment.percentage.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};

const HierarchicalSummaryTable = ({ hierarchy, categories, parentLabel, childLabel, collapsedParents, onToggleParent }) => (
  <div style={{ marginTop: 18, maxWidth: '100%', overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 820, fontSize: 14, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
      <thead>
        <tr style={{ background: '#f3f4f6' }}>
          <th style={{ textAlign: 'left', padding: '12px 14px' }}>{parentLabel} / {childLabel}</th>
          {categories.map(category => <th key={category} style={{ textAlign: 'center', padding: '12px 10px' }}>{category}</th>)}
          <th style={{ textAlign: 'center', padding: '12px 10px' }}>Base</th>
          <th style={{ textAlign: 'center', padding: '12px 10px' }}>% de {parentLabel.toLowerCase()}</th>
        </tr>
      </thead>
      <tbody>
        {hierarchy.flatMap(parent => {
          const parentRows = [
            <tr key={`parent-${parent.name}`} style={{ background: '#eff6ff', fontWeight: 800 }}>
              <td style={{ padding: '5px 8px' }}>
                <button
                  type="button"
                  aria-expanded={!collapsedParents.has(parent.name)}
                  onClick={() => onToggleParent(parent.name)}
                  style={{ width: '100%', padding: '6px', border: 0, background: 'transparent', color: '#172033', fontWeight: 800, cursor: 'pointer', textAlign: 'left' }}
                >
                  {collapsedParents.has(parent.name) ? '▸' : '⌄'}&nbsp;&nbsp;{parent.name}
                </button>
              </td>
              {categories.map(category => <td key={`${parent.name}-${category}`} style={{ textAlign: 'center', padding: '11px 10px' }}>{parent.counts[category] || 0}</td>)}
              <td style={{ textAlign: 'center', padding: '11px 10px' }}>{parent.base}</td>
              <td style={{ textAlign: 'center', padding: '11px 10px' }}>100.0%</td>
            </tr>
          ];
          const childRows = collapsedParents.has(parent.name) ? [] : parent.children.map((child, index) => (
            <tr key={`${parent.name}-${child.name}`} style={{ background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ padding: '10px 14px 10px 34px', borderBottom: '1px solid #eef2f7' }}>{child.name}</td>
              {categories.map(category => <td key={`${parent.name}-${child.name}-${category}`} style={{ textAlign: 'center', padding: '10px', borderBottom: '1px solid #eef2f7' }}>{child.counts[category] || 0}</td>)}
              <td style={{ textAlign: 'center', padding: '10px', borderBottom: '1px solid #eef2f7', fontWeight: 700 }}>{child.base}</td>
              <td style={{ textAlign: 'center', padding: '10px', borderBottom: '1px solid #eef2f7' }}>{parent.base > 0 ? ((child.base / parent.base) * 100).toFixed(1) : '0.0'}%</td>
            </tr>
          ));
          return [...parentRows, ...childRows];
        })}
      </tbody>
    </table>
  </div>
);

const DualFilterQuestionCard = ({ question, questionKey, selectedFilters, onToggleFilter, hierarchy, categories, colors, chartMode, onChartModeChange }) => {
  const [excludedValues, setExcludedValues] = useState({ parent: [], child: [] });
  const [collapsedParents, setCollapsedParents] = useState(() => new Set());
  const [parentKey, childKey] = selectedFilters;
  const parentLabel = FILTER_OPTIONS.find(option => option.key === parentKey)?.label || 'Nivel principal';
  const childLabel = FILTER_OPTIONS.find(option => option.key === childKey)?.label || 'Subnivel';
  const parentValues = hierarchy.map(parent => parent.name);
  const childValues = [...new Set(hierarchy.flatMap(parent => parent.children.map(child => child.name)))]
    .sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
  const visibleHierarchy = hierarchy
    .filter(parent => !excludedValues.parent.includes(parent.name))
    .map(parent => {
      const children = parent.children.filter(child => !excludedValues.child.includes(child.name));
      return {
        ...parent,
        children,
        base: children.reduce((sum, child) => sum + child.base, 0),
        counts: categories.reduce((acc, category) => ({
          ...acc,
          [category]: children.reduce((sum, child) => sum + (child.counts[category] || 0), 0)
        }), {})
      };
    })
    .filter(parent => parent.children.length > 0);
  const flatData = visibleHierarchy.flatMap(parent => parent.children.map(child => ({
    name: `${parent.name} · ${child.name}`,
    parentName: parent.name,
    childName: child.name,
    base: child.base,
    ...categories.reduce((acc, category) => ({ ...acc, [category]: child.counts[category] || 0 }), {})
  })));
  const chartWidth = Math.max(760, flatData.length * 145);
  const toggleParent = (parentName) => {
    setCollapsedParents(current => {
      const next = new Set(current);
      if (next.has(parentName)) next.delete(parentName);
      else next.add(parentName);
      return next;
    });
  };
  const renderVerticalTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0]?.payload;
    const total = categories.reduce((sum, category) => sum + (Number(row?.[category]) || 0), 0);
    return (
      <div style={{ minWidth: 250, padding: '12px 14px', border: '1px solid #dbe3ee', borderRadius: 10, background: '#fff', boxShadow: '0 12px 28px rgba(15,23,42,0.18)', fontSize: 13 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{row?.parentName} · {row?.childName}</div>
        <div><strong>{parentLabel}:</strong> {row?.parentName}</div>
        <div><strong>{childLabel}:</strong> {row?.childName}</div>
        <div style={{ marginTop: 8 }}><strong>Base del grupo:</strong> {row?.base || 0} respuestas</div>
        {payload.filter(item => Number(item.value) > 0).map(item => {
          const count = Number(item.value) || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return (
            <div key={item.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color }} />
              <span>{item.dataKey}: <strong>{count}</strong> ({percentage.toFixed(1)}%)</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div id={getQuestionAnchorId(questionKey)} data-analytics-section key={questionKey} className="analytics-detail-card analytics-question-card" style={{ marginBottom: '30px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <span style={{ fontSize: '1.35rem', fontWeight: 600 }}>{question.title || 'Pregunta'}</span>
      </div>
      <QuestionFilterControls selectedFilters={selectedFilters} onToggle={onToggleFilter} />
      <div className="analytics-view-toolbar">
        <span style={{ color: '#64748b', fontSize: 13 }}>
          <strong>{parentLabel}</strong> será el nivel principal · <strong>{childLabel}</strong> se mostrará en filas
        </span>
        <div style={{ display: 'inline-flex', padding: 3, border: '1px solid #dbe3ee', borderRadius: 9, background: '#f8fafc' }}>
          <button type="button" aria-pressed={chartMode === 'vertical'} onClick={() => onChartModeChange('vertical')} style={{ border: 0, borderRadius: 7, padding: '7px 12px', fontWeight: 700, cursor: 'pointer', color: chartMode === 'vertical' ? '#0b5ed7' : '#64748b', background: chartMode === 'vertical' ? '#e8f0ff' : 'transparent', display: 'inline-flex', alignItems: 'center', gap: 6 }}><BarChart3 size={15} aria-hidden="true" />Barras verticales</button>
          <button type="button" aria-pressed={chartMode === 'horizontal'} onClick={() => onChartModeChange('horizontal')} style={{ border: 0, borderRadius: 7, padding: '7px 12px', fontWeight: 700, cursor: 'pointer', color: chartMode === 'horizontal' ? '#0b5ed7' : '#64748b', background: chartMode === 'horizontal' ? '#e8f0ff' : 'transparent', display: 'inline-flex', alignItems: 'center', gap: 6 }}><BarChartHorizontal size={15} aria-hidden="true" />Barras horizontales</button>
        </div>
      </div>

      <div className="analytics-values-toolbar">
        <div className="analytics-values-toolbar-selectors">
          <HierarchyValueSelector
            idPrefix={`${questionKey}-parent`}
            label={parentLabel}
            values={parentValues}
            excludedValues={excludedValues.parent}
            onApply={(valuesToExclude) => setExcludedValues(current => ({ ...current, parent: valuesToExclude }))}
          />
          <HierarchyValueSelector
            idPrefix={`${questionKey}-child`}
            label={childLabel}
            values={childValues}
            excludedValues={excludedValues.child}
            onApply={(valuesToExclude) => setExcludedValues(current => ({ ...current, child: valuesToExclude }))}
          />
        </div>
        {chartMode === 'horizontal' && (
          <div className="analytics-values-toolbar-actions">
            <button type="button" onClick={() => setCollapsedParents(new Set(visibleHierarchy.map(parent => parent.name)))} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}><ChevronsUp size={14} aria-hidden="true" />Contraer todo</button>
            <button type="button" onClick={() => setCollapsedParents(new Set())} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}><ChevronsDown size={14} aria-hidden="true" />Expandir todo</button>
          </div>
        )}
      </div>

      {visibleHierarchy.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', fontSize: 16, padding: '60px 20px', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
          No hay valores visibles. Selecciona al menos una opción de {parentLabel.toLowerCase()} y {childLabel.toLowerCase()}.
        </div>
      ) : chartMode === 'horizontal' ? (
        <HorizontalHierarchyChart hierarchy={visibleHierarchy} categories={categories} colors={colors} parentLabel={parentLabel} childLabel={childLabel} collapsedParents={collapsedParents} onToggleParent={toggleParent} />
      ) : (
        <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', minHeight: 370 }}>
          <BarChart width={chartWidth} height={370} data={flatData} style={{ minWidth: 760 }} barCategoryGap="10%" margin={{ bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} angle={-18} textAnchor="end" height={100} tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis allowDecimals={false} />
            <Tooltip content={renderVerticalTooltip} />
            {categories.map((category, index) => (
              <Bar key={category} dataKey={category} fill={category === 'Sin responder' ? '#e0e0e0' : colors[index % colors.length]} />
            ))}
          </BarChart>
        </div>
      )}

      {visibleHierarchy.length > 0 && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', margin: '16px 0 8px' }}>
            {categories.map((category, index) => (
              <div key={category} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 14, height: 14, borderRadius: 4, border: '1px solid #d1d5db', background: category === 'Sin responder' ? '#e0e0e0' : colors[index % colors.length] }} />
                <span style={{ fontSize: 13 }}>{category}</span>
              </div>
            ))}
          </div>
          <HierarchicalSummaryTable hierarchy={visibleHierarchy} categories={categories} parentLabel={parentLabel} childLabel={childLabel} collapsedParents={collapsedParents} onToggleParent={toggleParent} />
        </>
      )}
    </div>
  );
};

const Analytics = () => {
  const [searchParams] = useSearchParams();
  // Eliminado import duplicado
  // Estado para filtros por pregunta
  const [questionFilters, setQuestionFilters] = useState({});
  const [questionChartModes, setQuestionChartModes] = useState({});
  const [responsesPagination, setResponsesPagination] = useState({});
  const [questionNavSearch, setQuestionNavSearch] = useState('');
  const [isQuestionNavOpen, setIsQuestionNavOpen] = useState(false);
  const [isQuestionNavCollapsed, setIsQuestionNavCollapsed] = useState(false);
  const [activeAnalyticsSection, setActiveAnalyticsSection] = useState('analytics-summary');
  const navigationTargetRef = useRef(null);
  const surveyId = searchParams.get('form');

  const [analytics, setAnalytics] = useState([]);
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Ocultar la barra de scroll global (verde) sólo en la página de Analítica
  useEffect(() => {
    document.body.classList.add('analytics-hide-scrollbar');
    return () => {
      document.body.classList.remove('analytics-hide-scrollbar');
    };
  }, []);

  useEffect(() => {
    if (surveyId) {
      fetchSurveyResponses(surveyId);
    }
  }, [surveyId]);

  useEffect(() => {
    if (!surveyId || !selectedSurvey) return undefined;
    let frame = null;

    const updateActiveSection = () => {
      frame = null;
      const navigationTarget = navigationTargetRef.current;
      if (navigationTarget && window.performance.now() < navigationTarget.expiresAt) return;
      if (navigationTarget) navigationTargetRef.current = null;

      const sections = Array.from(document.querySelectorAll('[data-analytics-section]'));
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const activationLine = Math.min(Math.max(viewportHeight * 0.28, 150), 260);
      const measuredSections = sections.map(section => ({
        section,
        rect: section.getBoundingClientRect()
      }));
      const sectionAtActivationLine = measuredSections.find(({ rect }) =>
        rect.top <= activationLine && rect.bottom > activationLine
      );
      const nearestVisibleSection = measuredSections
        .filter(({ rect }) => rect.bottom > 76 && rect.top < viewportHeight)
        .sort((a, b) =>
          Math.abs(a.rect.top - activationLine) - Math.abs(b.rect.top - activationLine)
        )[0];
      const activeSection = sectionAtActivationLine || nearestVisibleSection;

      if (activeSection?.section?.id) {
        setActiveAnalyticsSection(current =>
          current === activeSection.section.id ? current : activeSection.section.id
        );
      }
    };

    const scheduleActiveSectionUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(updateActiveSection);
    };

    const releaseNavigationTarget = () => {
      navigationTargetRef.current = null;
      scheduleActiveSectionUpdate();
    };

    scheduleActiveSectionUpdate();
    window.addEventListener('scroll', scheduleActiveSectionUpdate, { passive: true });
    window.addEventListener('resize', scheduleActiveSectionUpdate);
    window.addEventListener('wheel', releaseNavigationTarget, { passive: true });
    window.addEventListener('touchstart', releaseNavigationTarget, { passive: true });

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleActiveSectionUpdate);
      window.removeEventListener('resize', scheduleActiveSectionUpdate);
      window.removeEventListener('wheel', releaseNavigationTarget);
      window.removeEventListener('touchstart', releaseNavigationTarget);
    };
  }, [surveyId, selectedSurvey, surveyResponses.length]);

  useEffect(() => {
    if (!isQuestionNavOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsQuestionNavOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isQuestionNavOpen]);

  const fetchAnalytics = async () => {
    try {
      // Obtener analytics (conteos) y metadatos de forms (fechas, isActive, límites)
      const [analyticsRes, formsRes] = await Promise.all([
        api.get('/api/analytics/forms'),
        api.get('/api/forms')
      ]);
      const forms = Array.isArray(formsRes.data) ? formsRes.data : [];
      const formMap = {};
      forms.forEach(f => {
        const id = f._id || f.id;
        if (id) formMap[id] = f;
      });
      // Unir por id para asegurar que analytics tenga startDate/endDate/isActive/maxResponses
      const merged = (Array.isArray(analyticsRes.data) ? analyticsRes.data : []).map(a => {
        const id = a.id || a._id;
        const fm = id ? formMap[id] : null;
        return {
          ...a,
          id,
          // Preferir títulos/fechas desde forms si faltan
          title: a.title || fm?.title,
          createdAt: a.createdAt || fm?.createdAt,
          // Campos necesarios para calcular estado efectivo
          isActive: fm?.isActive ?? a.isActive,
          startDate: fm?.startDate ?? a.startDate,
          endDate: fm?.endDate ?? a.endDate,
          maxResponses: fm?.maxResponses ?? a.maxResponses,
          // Asegurar responseCount
          responseCount: typeof a.responseCount === 'number' ? a.responseCount : (fm?.responseCount ?? 0)
        };
      });
      setAnalytics(merged);
    } catch (error) {
      toast.error('No se pudo obtener los análisis');
    } finally {
      setLoading(false);
    }
  };

  // Tooltip con porcentaje junto a cantidad
  const makeTooltip = (dataArr = [], barKeys = []) => ({ active, label, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    // Total para porcentaje: si hay varias series (agrupado) usar payload; si no, usar suma total del campo único
    let total = 0;
    if (barKeys.length > 1) {
      total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
    } else {
      const key = barKeys[0] || (payload[0] && payload[0].dataKey) || 'Cantidad';
      total = (dataArr || []).reduce((s, d) => s + (Number(d?.[key]) || 0), 0);
    }
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }}>
        {label && <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>}
        {payload.map((p, idx) => {
          const val = Number(p.value) || 0;
          const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0.0';
          const name = p.name || p.dataKey;
          const color = p.color || p.fill || '#666';
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
              <span style={{ width: 10, height: 10, background: color, borderRadius: 2, border: '1px solid #ddd' }} />
              <span style={{ color, marginLeft: 8 }}>
                {name}:
              </span>
              <span style={{ color, marginLeft: 14 }}>{val}</span>
              <span style={{ color: '#6b7280', marginLeft: 14 }}>({pct}%)</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Helpers para normalizar respuestas y comparar con opciones
  const normalizeAnswer = (ans) => {
    if (ans === null || ans === undefined) return undefined;
    if (typeof ans === 'string' || typeof ans === 'number') return String(ans);
    if (Array.isArray(ans)) return ans.map(normalizeAnswer);
    if (typeof ans === 'object') {
      return ans.value || ans.label || ans.name || ans.title || ans.text || undefined;
    }
    return undefined;
  };



  // Mapear un valor de respuesta a etiqueta de opción si viene como índice (0,1,2,...)
  const toOptionLabel = (val, optionLabels) => {
    const s = normalizeAnswer(val);
    if (s === undefined) return undefined;
    const str = String(s).trim();
    if (/^\d+$/.test(str)) {
      const idx = Number(str);
      if (idx >= 0 && idx < optionLabels.length) return String(optionLabels[idx]);
    }
    return str;
  };

  // Comparar respuesta (string/obj/número/array) con una opción dada
  const matchesOption = (answer, option, optionLabels) => {
    const optNorm = String(option).trim().toLowerCase();
    if (Array.isArray(answer)) {
      const arr = answer.map(v => toOptionLabel(v, optionLabels)).filter(v => v !== undefined).map(v => v.trim().toLowerCase());
      return arr.includes(optNorm);
    }
    const lab = toOptionLabel(answer, optionLabels);
    if (lab === undefined) return false;
    return lab.trim().toLowerCase() === optNorm;
  };

  const fetchSurveyResponses = async (id) => {
    try {
      let response = await api.get(`/api/forms/${id}/responses`);
      let responses = response.data;
      // Si no hay respuestas (bienestar), intentar obtener desde SurveyEvaluation
      if (!responses.length) {
        try {
          const evalRes = await api.get(`/api/survey-evaluation/${id}`);
          responses = evalRes.data;
        } catch { }
      }
      // Normalizar estructura: algunos backends envían 'respuestas' en lugar de 'answers'
      const normalized = Array.isArray(responses) ? responses.map(r => {
        if (r && !r.answers && Array.isArray(r.respuestas)) {
          return { ...r, answers: r.respuestas };
        }
        return r;
      }) : responses;
      setSurveyResponses(normalized);
      // Get survey details
      const surveyResponse = await api.get(`/api/forms/${id}`);
      setSelectedSurvey(surveyResponse.data);
    } catch (error) {
      toast.error('No se pudieron obtener las respuestas de la encuesta');
    }
  };

  // Función general para detectar si una respuesta es válida
  // Casos especiales:
  // - slider: el valor 0 es una respuesta válida
  // - file_upload: objeto/url válido debe considerarse respondido; objetos vacíos no
  // - matrix: arreglos anidados sólo cuentan si al menos una celda tiene contenido
  const isAnswered = (r) => {
    if (r === null || r === undefined) return false;
    // strings: contenido no vacío
    if (typeof r === 'string') return r.trim() !== '';
    // números: cualquier número (incluye 0) es válido
    if (typeof r === 'number') return !Number.isNaN(r);
    // boolean: no suele usarse, pero se considera no respondido salvo true explícito
    if (typeof r === 'boolean') return r === true;
    // arrays: alguna entrada no vacía
    if (Array.isArray(r)) return r.some(item => isAnswered(item));
    // objetos: detectar campos típicos de archivo o cualquier subvalor no vacío
    if (typeof r === 'object') {
      // file_upload comunes
      const fileHints = ['url', 'path', 'filename', 'name', 'file', 'originalname'];
      for (const k of fileHints) {
        if (k in r && isAnswered(r[k])) return true;
      }
      // genérico: algún valor interno no vacío
      const vals = Object.values(r);
      return vals.length > 0 && vals.some(isAnswered);
    }
    // fallback
    return false;
  };



  const getTimeSeriesData = () => {
    if (!surveyResponses.length) return [];

    const dailyCounts = {};
    surveyResponses.forEach(response => {
      const date = new Date(response.submittedAt).toLocaleDateString();
      dailyCounts[date] = (dailyCounts[date] || 0) + 1;
    });

    // Ordenar las fechas cronológicamente
    return Object.entries(dailyCounts)
      .sort(([dateA], [dateB]) => {
        // Convertir las fechas de string a Date para comparar correctamente
        const parsedDateA = new Date(dateA.split('/').reverse().join('-')); // dd/mm/yyyy -> yyyy-mm-dd
        const parsedDateB = new Date(dateB.split('/').reverse().join('-'));
        return parsedDateA - parsedDateB;
      })
      .map(([date, count]) => ({
        date,
        Respuestas: count
      }));
  };



  // Tick personalizado: etiquetas horizontales, centradas y con salto de línea si hay varias palabras
  const WrappedTick = React.memo(({ x, y, payload }) => {
    const value = String(payload?.value ?? '');
    const words = value.split(/\s+/).filter(Boolean);
    // dividir en 2 líneas si tiene más de una palabra
    const mid = Math.ceil(words.length / 2);
    const top = words.slice(0, mid).join(' ');
    const bottom = words.slice(mid).join(' ');
    const hasTwo = bottom.length > 0;
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor="middle" fill="#666" fontSize={12}>
          {hasTwo ? (
            <>
              <tspan x={0} dy={-2}>{top}</tspan>
              <tspan x={0} dy={14}>{bottom}</tspan>
            </>
          ) : (
            <tspan x={0} dy={8}>{value}</tspan>
          )}
        </text>
      </g>
    );
  });

  // Calcular ancho dinámico del gráfico según cantidad y longitud de etiquetas
  const getChartWidth = (arr = []) => {
    const names = arr.map(d => String(d?.name ?? ''));
    const maxLen = names.reduce((m, s) => Math.max(m, s.length), 0);
    const hasMulti = names.some(s => s.includes(' '));
    // Aumentamos factores para dar más espacio a etiquetas largas y muchas categorías
    let factor = 100;
    if (hasMulti || maxLen > 8) factor = 130;
    if (maxLen > 16) factor = 150;
    if (names.length > 10) factor += 10; // más categorías => más espacio
    return Math.max(560, factor * names.length);
  };

  // Tamaño de barra según cantidad de categorías y series (agrupaciones)
  const getBarSize = (count = 1, series = 1) => {
    const n = Math.max(1, count);
    const s = Math.max(1, series);
    // base más grande y reducimos cuando hay muchas categorías o varias series
    let base = 36;
    if (n > 12) base = 22;
    else if (n > 9) base = 26;
    else if (n > 6) base = 30;
    // dividir por series para evitar solapamientos cuando hay agrupación
    const size = Math.max(16, Math.floor(base / s));
    return size;
  };

  // Helper para leer valores de filtro con posibles variantes de estructura
  const getFilterValue = (resp, key) => {
    if (!resp || !key) return undefined;
    const pick = (obj, k) => (obj ? obj[k] : undefined);
    switch (key) {
      case 'company':
        return (
          pick(resp, 'company') || pick(resp, 'empresa') || pick(resp, 'companyName') ||
          (resp.user && (resp.user.company || resp.user.empresa || resp.user.companyName)) ||
          (resp.profile && (resp.profile.company || resp.profile.empresa))
        );
      case 'sede':
        return pick(resp, 'sede') || (resp.user && resp.user.sede) || (resp.profile && resp.profile.sede) || pick(resp, 'site');
      case 'department':
        return pick(resp, 'department') || pick(resp, 'departament') || pick(resp, 'departamento') || (resp.user && (resp.user.department || resp.user.departamento));
      case 'area':
        return pick(resp, 'area') || (resp.user && resp.user.area) || (resp.profile && resp.profile.area);
      case 'section':
        return pick(resp, 'section') || pick(resp, 'seccion') || (resp.user && (resp.user.section || resp.user.seccion));
      case 'occupation':
        return pick(resp, 'occupation') || pick(resp, 'ocupacion') || (resp.user && (resp.user.occupation || resp.user.ocupacion));
      default:
        return resp[key];
    }
  };

  const getSelectedQuestionFilters = (questionKey) => normalizeSelectedFilters(questionFilters[questionKey]);

  const toggleQuestionFilter = (questionKey, filterKey) => {
    const selectedFilters = getSelectedQuestionFilters(questionKey);

    if (!filterKey) {
      setQuestionFilters(current => ({ ...current, [questionKey]: [] }));
      return;
    }

    if (selectedFilters.includes(filterKey)) {
      setQuestionFilters(current => ({
        ...current,
        [questionKey]: selectedFilters.filter(key => key !== filterKey)
      }));
      return;
    }

    if (selectedFilters.length >= 2) {
      toast.error('Puedes seleccionar como máximo 2 filtros.');
      return;
    }

    const nextFilters = normalizeSelectedFilters([...selectedFilters, filterKey]);
    setQuestionFilters(current => ({ ...current, [questionKey]: nextFilters }));
    if (nextFilters.length === 2) {
      setQuestionChartModes(current => ({
        ...current,
        [questionKey]: current[questionKey] || 'horizontal'
      }));
    }
  };

  const buildHierarchyData = (selectedFilters, categories, resolveAnswerCategories, responses) => {
    const [parentKey, childKey] = selectedFilters;
    const grouped = new Map();
    const createCounts = () => categories.reduce((acc, category) => ({ ...acc, [category]: 0 }), {});
    const getGroupLabel = (response, key) => {
      const normalized = normalizeAnswer(getFilterValue(response, key));
      const label = Array.isArray(normalized) ? normalized.join(', ') : normalized;
      return label === undefined || String(label).trim() === '' ? 'Sin especificar' : String(label).trim();
    };

    surveyResponses.forEach((response, responseIndex) => {
      const parentName = getGroupLabel(response, parentKey);
      const childName = getGroupLabel(response, childKey);
      const answerCategories = [...new Set(resolveAnswerCategories(responses[responseIndex]))]
        .filter(category => categories.includes(category));
      const effectiveCategories = answerCategories.length > 0 ? answerCategories : ['Sin responder'];

      if (!grouped.has(parentName)) {
        grouped.set(parentName, {
          name: parentName,
          base: 0,
          counts: createCounts(),
          children: new Map()
        });
      }

      const parent = grouped.get(parentName);
      if (!parent.children.has(childName)) {
        parent.children.set(childName, {
          name: childName,
          base: 0,
          counts: createCounts()
        });
      }

      const child = parent.children.get(childName);
      parent.base += 1;
      child.base += 1;
      effectiveCategories.forEach(category => {
        parent.counts[category] = (parent.counts[category] || 0) + 1;
        child.counts[category] = (child.counts[category] || 0) + 1;
      });
    });

    return Array.from(grouped.values())
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true }))
      .map(parent => ({
        ...parent,
        children: Array.from(parent.children.values())
          .sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true }))
      }));
  };

  // Función para exportar respuestas a Excel
  const exportToExcel = () => {
    if (!selectedSurvey || !surveyResponses.length) return;

    // Crear estructura de preguntas con índices globales
    const allQuestions = [];
    let globalIndex = 0;

    if (Array.isArray(selectedSurvey.pages) && selectedSurvey.pages.length > 0) {
      selectedSurvey.pages.forEach((page, pageIdx) => {
        (page.elements || page.questions || []).forEach((question, qIdx) => {
          allQuestions.push({
            ...question,
            globalIndex,
            pageIdx,
            qIdx,
            title: question.title || question.name || `Pregunta ${globalIndex + 1}`
          });
          globalIndex++;
        });
      });
    } else if (Array.isArray(selectedSurvey.questions)) {
      selectedSurvey.questions.forEach((question, qIdx) => {
        allQuestions.push({
          ...question,
          globalIndex: qIdx,
          pageIdx: 0,
          qIdx,
          title: question.title || question.name || `Pregunta ${qIdx + 1}`
        });
      });
    }

    const headers = ['Fecha de Envío', 'Empresa', 'Sede', 'Departamento', 'Área', 'Sección', ...allQuestions.map(q => q.title)];

    const rows = surveyResponses.map(response => {
      const row = [
        response.submittedAt ? new Date(response.submittedAt).toLocaleString('es-ES') : 'N/A',
        response.company || 'N/A',
        response.sede || 'N/A',
        response.department || 'N/A',
        response.area || 'N/A',
        response.section || 'N/A'
      ];

      // Obtener respuesta para cada pregunta
      allQuestions.forEach(question => {
        let answer = '';

        if (Array.isArray(response.answers)) {
          // Buscar por questionId primero
          const foundByQuestionId = response.answers.find(a =>
            a && typeof a === 'object' && a.questionId === question.id
          );

          if (foundByQuestionId) {
            answer = foundByQuestionId.answer || '';
          } else if (response.answers[question.globalIndex]) {
            // Usar índice global
            const resp = response.answers[question.globalIndex];
            answer = typeof resp === 'object' ? resp.answer : resp;
          }
        }

        // Formatear arrays como texto separado por comas
        if (Array.isArray(answer)) {
          answer = answer.join(', ');
        }

        row.push(answer || '');
      });

      return row;
    });
    // Construir hoja y libro
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    // Ajustar ancho de columnas automáticamente
    const colWidths = headers.map((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...rows.map(row => (row[i] ? row[i].toString().length : 0))
      );
      return { wch: Math.min(Math.max(maxLen + 2, 16), 60) }; // ancho mínimo 16, máximo 60
    });
    ws['!cols'] = colWidths;
    // Congelar la primera fila
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    // Crear libro y exportar
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Respuestas');
    XLSX.writeFile(wb, `respuestas_encuesta_${selectedSurvey.title || selectedSurvey.id}.xlsx`);
  };

  const navigationQuestions = selectedSurvey && Array.isArray(selectedSurvey.pages)
    ? selectedSurvey.pages.flatMap((page, pageIdx) =>
      (page.elements || page.questions || []).map((question, questionIndex) => ({
        key: question.id || `${pageIdx}-${questionIndex}`,
        title: getNavigationQuestionTitle(question.title || question.name || `Pregunta ${questionIndex + 1}`),
        pageTitle: page.title || page.name || null
      }))
    ).map((question, index) => ({ ...question, number: index + 1 }))
    : [];
  const normalizedNavSearch = questionNavSearch.trim().toLocaleLowerCase('es');
  const visibleNavigationQuestions = navigationQuestions.filter(question =>
    !normalizedNavSearch ||
    question.title.toLocaleLowerCase('es').includes(normalizedNavSearch) ||
    String(question.number).includes(normalizedNavSearch)
  );
  const navigateToAnalyticsSection = (sectionId) => {
    const target = document.getElementById(sectionId);
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    navigationTargetRef.current = {
      id: sectionId,
      expiresAt: window.performance.now() + (reduceMotion ? 100 : 1400)
    };
    setActiveAnalyticsSection(sectionId);
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    setIsQuestionNavOpen(false);
  };

  // DEBUG LOGS
  // Todas las líneas de console.log y console.error han sido eliminadas

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  return (
    <div className="dashboard-bg">
      <div className={`dashboard-header ${surveyId ? `analytics-detail-header ${isQuestionNavCollapsed ? 'is-nav-collapsed' : ''}` : ''}`}>
        <h1 className="dashboard-title"><BarChart3 size={30} strokeWidth={1.8} aria-hidden="true" />Análisis Dashboard</h1>
        {surveyId && (
          <button onClick={exportToExcel} className="analytics-export-btn">
            <FileSpreadsheet size={18} aria-hidden="true" />Exportar a Excel
          </button>
        )}
      </div>

      {!surveyId ? (
        // Overview analytics
        <div className="analytics-content">
          <div className="analytics-cards-row">
            <div className="analytics-card">
              <div className="analytics-card-title">
                <BarChart3 size={20} />
                Total de Encuestas
              </div>
              <div className="analytics-card-value">{analytics.length}</div>
            </div>

            <div className="analytics-card">
              <div className="analytics-card-title">
                <Users size={20} />
                Total de Respuestas
              </div>
              <div className="analytics-card-value">
                {analytics.reduce((sum, survey) => sum + survey.responseCount, 0)}
              </div>
            </div>

            <div className="analytics-card">
              <div className="analytics-card-title">
                <TrendingUp size={20} />
                Encuestas Activas
              </div>
              <div className="analytics-card-value">
                {analytics.filter(survey => {
                  const maxReached = survey.maxResponses && survey.responseCount >= Number(survey.maxResponses);
                  const now = new Date();
                  const start = survey.startDate ? new Date(survey.startDate) : null;
                  const end = survey.endDate ? new Date(survey.endDate) : null;
                  const hasScheduleOrMax = !!(survey.startDate || survey.endDate || maxReached);
                  const outOfWindow = maxReached || (start && now < start) || (end && now > end);
                  const effectiveActive = hasScheduleOrMax ? !outOfWindow : !!survey.isActive;
                  return effectiveActive;
                }).length}
              </div>
            </div>
          </div>

          <div className="analytics-table-block">
            <h3 className="analytics-table-title">Encuestas</h3>
            <div className="analytics-table-wrapper">
              <table className="analytics-table">
                <thead className="analytics-table-header">
                  <tr>
                    <th>Título</th>
                    <th>Respuestas</th>
                    <th>Estado</th>
                    <th>Fecha de creación</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.map((survey) => (
                    <tr key={survey.id} className="analytics-table-row">
                      <td>{survey.title}</td>
                      <td style={{ textAlign: 'center' }}>{survey.responseCount}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          const maxReached = survey.maxResponses && survey.responseCount >= Number(survey.maxResponses);
                          const now = new Date();
                          const start = survey.startDate ? new Date(survey.startDate) : null;
                          const end = survey.endDate ? new Date(survey.endDate) : null;
                          const hasScheduleOrMax = !!(survey.startDate || survey.endDate || maxReached);
                          const outOfWindow = maxReached || (start && now < start) || (end && now > end);
                          const effectiveActive = hasScheduleOrMax ? !outOfWindow : !!survey.isActive;
                          return (
                            <span className={"survey-badge " + (effectiveActive ? 'active' : 'inactive')}>
                              {effectiveActive ? 'Activo' : 'Inactivo'}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {new Date(survey.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        // Detailed survey analytics
        <div className={`analytics-detail-shell ${isQuestionNavCollapsed ? 'is-nav-collapsed' : ''}`}>
          <button
            type="button"
            className="analytics-mobile-nav-toggle"
            aria-label="Abrir navegación de preguntas"
            aria-expanded={isQuestionNavOpen}
            onClick={() => {
              setIsQuestionNavCollapsed(false);
              setIsQuestionNavOpen(true);
            }}
          >
            <Menu size={19} aria-hidden="true" />
            Preguntas
          </button>
          {isQuestionNavOpen && (
            <button type="button" className="analytics-nav-overlay" aria-label="Cerrar navegación de preguntas" onClick={() => setIsQuestionNavOpen(false)} />
          )}
          <nav className={`analytics-question-nav ${isQuestionNavOpen ? 'is-open' : ''}`} aria-label="Navegación de preguntas">
            <div className="analytics-question-nav-header">
              <div className="analytics-question-nav-heading">
                <LayoutList size={19} aria-hidden="true" />
                {!isQuestionNavCollapsed && <span>Contenido</span>}
              </div>
              <button
                type="button"
                className="analytics-question-nav-collapse"
                aria-label={isQuestionNavCollapsed ? 'Expandir navegación' : 'Contraer navegación'}
                onClick={() => setIsQuestionNavCollapsed(current => !current)}
              >
                {isQuestionNavCollapsed ? <PanelLeftOpen size={18} aria-hidden="true" /> : <PanelLeftClose size={18} aria-hidden="true" />}
              </button>
              <button type="button" className="analytics-question-nav-mobile-close" aria-label="Cerrar navegación" onClick={() => setIsQuestionNavOpen(false)}>
                <X size={19} aria-hidden="true" />
              </button>
            </div>

            {!isQuestionNavCollapsed && (
              <label className="analytics-question-search">
                <Search size={16} aria-hidden="true" />
                <input
                  type="search"
                  value={questionNavSearch}
                  onChange={(event) => setQuestionNavSearch(event.target.value)}
                  placeholder="Buscar pregunta"
                  aria-label="Buscar pregunta"
                />
              </label>
            )}

            <div className="analytics-question-nav-list">
              <button
                type="button"
                className={`analytics-question-nav-item ${activeAnalyticsSection === 'analytics-summary' ? 'is-active' : ''}`}
                aria-current={activeAnalyticsSection === 'analytics-summary' ? 'location' : undefined}
                title="Resumen de la encuesta"
                onClick={() => navigateToAnalyticsSection('analytics-summary')}
              >
                <LayoutDashboard size={17} aria-hidden="true" />
                {!isQuestionNavCollapsed && <span>Resumen</span>}
              </button>
              {surveyResponses.length > 0 && (
                <button
                  type="button"
                  className={`analytics-question-nav-item ${activeAnalyticsSection === 'analytics-timeline' ? 'is-active' : ''}`}
                  aria-current={activeAnalyticsSection === 'analytics-timeline' ? 'location' : undefined}
                  title="Línea de tiempo"
                  onClick={() => navigateToAnalyticsSection('analytics-timeline')}
                >
                  <Activity size={17} aria-hidden="true" />
                  {!isQuestionNavCollapsed && <span>Línea de tiempo</span>}
                </button>
              )}

              {!isQuestionNavCollapsed && navigationQuestions.length > 0 && <div className="analytics-question-nav-section-label">Preguntas</div>}
              {visibleNavigationQuestions.map(question => {
                const sectionId = getQuestionAnchorId(question.key);
                const isActive = activeAnalyticsSection === sectionId;
                return (
                  <button
                    type="button"
                    key={question.key}
                    className={`analytics-question-nav-item analytics-question-nav-question ${isActive ? 'is-active' : ''}`}
                    aria-current={isActive ? 'location' : undefined}
                    title={`${question.number}. ${question.title}`}
                    onClick={() => navigateToAnalyticsSection(sectionId)}
                  >
                    <span className="analytics-question-nav-number">{question.number}</span>
                    {!isQuestionNavCollapsed && <span className="analytics-question-nav-label">{question.title}</span>}
                  </button>
                );
              })}
              {!isQuestionNavCollapsed && visibleNavigationQuestions.length === 0 && (
                <div className="analytics-question-nav-empty">No se encontraron preguntas.</div>
              )}
            </div>
            {!isQuestionNavCollapsed && (
              <div className="analytics-question-nav-footer">{navigationQuestions.length} preguntas</div>
            )}
          </nav>

          <main className="analytics-detail-main">
          {selectedSurvey && (
            <div id="analytics-summary" data-analytics-section className="analytics-detail-card analytics-survey-summary" style={{ marginBottom: '30px' }}>
              <h2 style={{ marginBottom: '10px' }}>{selectedSurvey.title}</h2>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                {selectedSurvey.description}
              </p>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Users size={16} />
                  <span>{surveyResponses.length} Respuestas</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Calendar size={16} />
                  <span>Fecha de creación: {new Date(selectedSurvey.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          )}

          {surveyResponses.length > 0 && (
            <>
              <div id="analytics-timeline" data-analytics-section className="analytics-detail-card analytics-timeline-card" style={{ marginBottom: '30px' }}>
                <h3 className="analytics-card-heading"><Activity size={19} aria-hidden="true" />Línea de tiempo de respuestas</h3>
                <div className="analytics-chart-scroll">
                  <ResponsiveContainer width="100%" height={340} debounce={80}>
                    <LineChart data={getTimeSeriesData()} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="Respuestas" stroke="#007bff" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {/* Gráficos por pregunta: recorrer páginas y preguntas con índices reales */}
              {selectedSurvey && Array.isArray(selectedSurvey.pages) && selectedSurvey.pages.length > 0 &&
                selectedSurvey.pages.map((page, pageIdx) =>
                  (page.elements || page.questions || []).map((question, qIdx) => {
                    const responses = surveyResponses.map(response => {
                      // Buscar la respuesta específica para esta pregunta
                      let answer = undefined;

                      if (Array.isArray(response.answers)) {
                        // Método 1: Buscar por questionId (más confiable)
                        const foundByQuestionId = response.answers.find(a =>
                          a && typeof a === 'object' && a.questionId === question.id
                        );

                        if (foundByQuestionId) {
                          answer = foundByQuestionId.answer;
                        } else {
                          // Método 2: Calcular índice global único
                          let globalQuestionIndex = 0;
                          let found = false;

                          // Recorrer todas las páginas hasta encontrar la pregunta actual
                          for (let pIdx = 0; pIdx < selectedSurvey.pages.length && !found; pIdx++) {
                            const currentPage = selectedSurvey.pages[pIdx];
                            const pageQuestions = currentPage.elements || currentPage.questions || [];

                            for (let qIdxInner = 0; qIdxInner < pageQuestions.length; qIdxInner++) {
                              if (pIdx === pageIdx && qIdxInner === qIdx && pageQuestions[qIdxInner].id === question.id) {
                                // Encontramos la pregunta actual
                                if (response.answers[globalQuestionIndex]) {
                                  const resp = response.answers[globalQuestionIndex];
                                  answer = typeof resp === 'object' ? resp.answer : resp;
                                }
                                found = true;
                                break;
                              }
                              globalQuestionIndex++;
                            }
                          }
                        }
                      }

                      return answer;
                    });

                    // Definir clave única de pregunta para filtros
                    const qKey = question.id || `${pageIdx}-${qIdx}`;
                    const selectedFiltersForQuestion = getSelectedQuestionFilters(qKey);

                    if (selectedFiltersForQuestion.length === 2) {
                      let categories;
                      let resolveAnswerCategories;

                      if ((question.type === 'multiple_choice' || question.type === 'checkbox') && Array.isArray(question.options)) {
                        const optionLabels = question.options.map(option =>
                          typeof option === 'string'
                            ? option
                            : (option?.value || option?.label || option?.name || option?.title || option?.text || '')
                        ).filter(Boolean);
                        categories = [...optionLabels, 'Sin responder'];
                        resolveAnswerCategories = (answer) => {
                          const matchedOptions = optionLabels.filter(option => matchesOption(answer, option, optionLabels));
                          return matchedOptions.length > 0 ? matchedOptions : ['Sin responder'];
                        };
                      } else if (question.type === 'scale') {
                        const min = Number(question.min ?? 1);
                        const max = Number(question.max ?? 5);
                        const configuredStep = Number(question.step ?? 1);
                        const step = Number.isFinite(configuredStep) && configuredStep > 0 ? configuredStep : 1;
                        const scaleValues = [];
                        for (let value = min; value <= max; value += step) {
                          scaleValues.push(String(value));
                        }
                        categories = [...scaleValues, 'Sin responder'];
                        resolveAnswerCategories = (answer) => {
                          const normalized = normalizeAnswer(answer);
                          const value = normalized === undefined ? '' : String(normalized).trim();
                          return scaleValues.includes(value) ? [value] : ['Sin responder'];
                        };
                      } else {
                        categories = ['Respondidas', 'Sin responder'];
                        resolveAnswerCategories = (answer) => [isAnswered(answer) ? 'Respondidas' : 'Sin responder'];
                      }

                      const hierarchy = buildHierarchyData(
                        selectedFiltersForQuestion,
                        categories,
                        resolveAnswerCategories,
                        responses
                      );

                      return (
                        <DualFilterQuestionCard
                          key={qKey}
                          question={{ ...question, title: question.title || `Pregunta ${qIdx + 1}` }}
                          questionKey={qKey}
                          selectedFilters={selectedFiltersForQuestion}
                          onToggleFilter={(filterKey) => toggleQuestionFilter(qKey, filterKey)}
                          hierarchy={hierarchy}
                          categories={categories}
                          colors={ANALYTICS_COLORS}
                          chartMode={questionChartModes[qKey] || 'horizontal'}
                          onChartModeChange={(mode) => setQuestionChartModes(current => ({ ...current, [qKey]: mode }))}
                        />
                      );
                    }

                    // Si la pregunta es de opción única (multiple_choice), mostrar gráfico de barras por opción
                    if (question.type === 'multiple_choice' && Array.isArray(question.options)) {
                      // Normalizar etiquetas de opciones (soporta objetos con value/label)
                      const optionLabels = question.options.map(opt =>
                        typeof opt === 'string' ? opt : (opt?.value || opt?.label || opt?.name || opt?.title || opt?.text || '')
                      );
                      // Contar respuestas por opción (normalizado)
                      const optionCounts = {};
                      optionLabels.forEach(option => {
                        optionCounts[option] = responses.filter(r => matchesOption(r, option, optionLabels)).length;
                      });
                      // Contar sin responder
                      const notAnsweredCount = responses.filter(r => !r || r === '').length;
                      // Paleta de colores
                      const COLORS = ['#FFBB28', '#0088FE', '#00C49F', '#FF8042', '#8884D8', '#82CA9D', '#FF6666', '#A28CFF', '#FFB347', '#B6FFB3'];
                      // Data con índice
                      const data = [
                        ...optionLabels.map((option, i) => ({ name: option, Cantidad: optionCounts[option], color: COLORS[i % COLORS.length] })),
                        { name: 'Sin responder', Cantidad: notAnsweredCount, color: '#e0e0e0' }
                      ];
                      const filterOptions = [
                        { key: null, label: 'Ninguno' },
                        { key: 'company', label: 'Empresa' },
                        { key: 'sede', label: 'Sede' },
                        { key: 'department', label: 'Departamento' },
                        { key: 'area', label: 'Área' },
                        { key: 'section', label: 'Sección' },
                        { key: 'occupation', label: 'Ocupación' }
                      ];
                      const selectedFilter = selectedFiltersForQuestion[0] || null;
                      let chartData = data;
                      let barKeys = ['Cantidad'];
                      if (selectedFilter) {
                        // Agrupado por filtro y opción, incluyendo 'Sin responder'
                        const grouped = {};
                        surveyResponses.forEach((resp, idx) => {
                          const filterValue = getFilterValue(resp, selectedFilter);
                          if (!filterValue) return;
                          const answer = responses[idx];
                          if (!grouped[filterValue]) grouped[filterValue] = {};
                          let matched = false;
                          optionLabels.forEach(option => {
                            if (!grouped[filterValue][option]) grouped[filterValue][option] = 0;
                            if (matchesOption(answer, option, optionLabels)) {
                              grouped[filterValue][option]++;
                              matched = true;
                            }
                          });
                          if (!matched) {
                            grouped[filterValue]['Sin responder'] = (grouped[filterValue]['Sin responder'] || 0) + 1;
                          }
                        });
                        chartData = Object.entries(grouped).map(([filterValue, optionsObj]) => {
                          const entry = { name: filterValue };
                          optionLabels.forEach(option => {
                            entry[option] = optionsObj[option] || 0;
                          });
                          entry['Sin responder'] = optionsObj['Sin responder'] || 0;
                          return entry;
                        });
                        barKeys = [...optionLabels, 'Sin responder'];
                      }
                      return (
                        <div id={getQuestionAnchorId(qKey)} data-analytics-section key={question.id || `${pageIdx}-${qIdx}`} className="analytics-detail-card analytics-question-card" style={{ marginBottom: '30px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <span style={{ fontSize: '1.35rem', fontWeight: 600 }}>{question.title || `Pregunta ${qIdx + 1}`}</span>
                          </div>
                          <QuestionFilterControls
                            selectedFilters={selectedFiltersForQuestion}
                            onToggle={(filterKey) => toggleQuestionFilter(qKey, filterKey)}
                          />
                          <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', minHeight: 350, marginTop: 12 }}>
                            {chartData.length === 0 ? (
                              <div style={{ textAlign: 'center', color: '#888', fontSize: 18, marginTop: 60 }}>
                                No hay datos para el filtro seleccionado.
                              </div>
                            ) : (
                              <BarChart width={getChartWidth(chartData)} height={360} data={chartData} style={{ minWidth: 480 }} barCategoryGap="10%" barSize={getBarSize(chartData.length, barKeys.length)} margin={{ bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" interval={0} tick={<WrappedTick />} height={60} tickMargin={8} />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={makeTooltip(chartData, barKeys)} />
                                {barKeys.length === 1 ? (
                                  <Bar dataKey="Cantidad">
                                    {chartData.map((entry, i) => (
                                      <Cell key={`cell-mc-${i}`} fill={entry.color || COLORS[i % COLORS.length]} />
                                    ))}
                                  </Bar>
                                ) : (
                                  barKeys.map((key, idx) => (
                                    <Bar key={key} dataKey={key} fill={key === 'Sin responder' ? '#e0e0e0' : COLORS[idx % COLORS.length]} />
                                  ))
                                )}
                              </BarChart>
                            )}
                          </div>
                          {/* Leyenda personalizada: centrada y con menor separación */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', margin: '10px 0 14px' }}>
                            {optionLabels.map((option, i) => (
                              <div key={option} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ display: 'inline-block', width: 18, height: 18, background: COLORS[i % COLORS.length], borderRadius: 4, border: '1px solid #ccc' }}></span>
                                <span>{option}</span>
                              </div>
                            ))}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 18, height: 18, background: '#e0e0e0', borderRadius: 4, border: '1px solid #ccc' }}></span>
                              <span>Sin responder</span>
                            </div>
                          </div>
                          {/* Tabla resumen de opciones (dinámica por filtro) */}
                          <div style={{ marginTop: 8, padding: '0 8px', maxWidth: '100%', overflowX: 'auto' }}>
                            {selectedFilter ? (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 700 }}>{filterOptions.find(o => o.key === selectedFilter)?.label || 'Filtro'}</th>
                                    {optionLabels.map(opt => (
                                      <th key={`th-${opt}`} style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>{opt}</th>
                                    ))}
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>Sin responder</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>Total</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const grouped = {};
                                    surveyResponses.forEach((resp, idx) => {
                                      const fv = getFilterValue(resp, selectedFilter);
                                      if (!fv) return;
                                      const ans = responses[idx];
                                      if (!grouped[fv]) grouped[fv] = { __na: 0 };
                                      let matched = false;
                                      optionLabels.forEach(opt => {
                                        grouped[fv][opt] = grouped[fv][opt] || 0;
                                        if (matchesOption(ans, opt, optionLabels)) {
                                          grouped[fv][opt] += 1;
                                          matched = true;
                                        }
                                      });
                                      if (!matched) grouped[fv].__na += 1;
                                    });
                                    const entries = Object.entries(grouped);
                                    const rows = entries.map(([fv, obj], i) => {
                                      const na = obj.__na || 0;
                                      const rowTotal = optionLabels.reduce((s, o) => s + (obj[o] || 0), 0) + na;
                                      const pct = surveyResponses.length > 0 ? ((rowTotal / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                      return (
                                        <tr key={`mc-row-${fv}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{fv}</td>
                                          {optionLabels.map(opt => (
                                            <td key={`td-${fv}-${opt}`} style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{obj[opt] || 0}</td>
                                          ))}
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{na}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{rowTotal}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{pct}%</td>
                                        </tr>
                                      );
                                    });
                                    // totales
                                    const totals = optionLabels.reduce((acc, o) => ({ ...acc, [o]: entries.reduce((s, [, v]) => s + (v[o] || 0), 0) }), {});
                                    const totalNA = entries.reduce((s, [, v]) => s + (v.__na || 0), 0);
                                    const grand = Object.values(totals).reduce((s, n) => s + n, 0) + totalNA;
                                    return [
                                      ...rows,
                                      <tr key="mc-total" style={{ background: '#eef6ff', fontWeight: 800, borderTop: '2px solid #93c5fd' }}>
                                        <td style={{ padding: '12px 14px', color: '#111827' }}>Total</td>
                                        {optionLabels.map(opt => (
                                          <td key={`tot-${opt}`} style={{ textAlign: 'center', padding: '12px 14px' }}>{totals[opt]}</td>
                                        ))}
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>{totalNA}</td>
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>{grand}</td>
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>100.0%</td>
                                      </tr>
                                    ];
                                  })()}
                                </tbody>
                              </table>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>Opción</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>Cantidad</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {question.options.map((option, i) => {
                                    const count = optionCounts[option];
                                    const percent = surveyResponses.length > 0 ? ((count / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                    return (
                                      <tr key={option} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{option}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{count}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{percent}%</td>
                                      </tr>
                                    );
                                  })}
                                  {/* Sin responder */}
                                  <tr style={{ background: question.options.length % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '10px 12px' }}>Sin responder</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{notAnsweredCount}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{surveyResponses.length > 0 ? ((notAnsweredCount / surveyResponses.length) * 100).toFixed(1) : '0.0'}%</td>
                                  </tr>
                                  {/* Total */}
                                  <tr style={{ background: '#eef6ff', fontWeight: 800 }}>
                                    <td style={{ padding: '12px 14px', color: '#111827' }}>Total</td>
                                    <td style={{ textAlign: 'center', padding: '12px 14px' }}>{surveyResponses.length}</td>
                                    <td style={{ textAlign: 'center', padding: '12px 14px' }}>100.0%</td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Si la pregunta es de opción múltiple (checkbox), mostrar gráfico de barras por opción
                    if (question.type === 'checkbox' && Array.isArray(question.options)) {
                      // Contar respuestas por opción (visión general)
                      const optionLabels = question.options.map(opt =>
                        typeof opt === 'string' ? opt : (opt?.value || opt?.label || opt?.name || opt?.title || opt?.text || '')
                      );
                      const optionCounts = {};
                      optionLabels.forEach(option => {
                        optionCounts[option] = responses.filter(r => matchesOption(r, option, optionLabels)).length;
                      });
                      // Contar sin responder (ninguna opción marcada)
                      const notAnsweredCount = responses.filter(r => !Array.isArray(r) || r.length === 0).length;
                      // Paleta de colores
                      const COLORS = ['#FFBB28', '#0088FE', '#00C49F', '#FF8042', '#8884D8', '#82CA9D', '#FF6666', '#A28CFF', '#FFB347', '#B6FFB3'];
                      // Data para el gráfico por defecto
                      let data = [
                        ...optionLabels.map((option, i) => ({ name: option, Cantidad: optionCounts[option], color: COLORS[i % COLORS.length] })),
                        { name: 'Sin responder', Cantidad: notAnsweredCount, color: '#e0e0e0' }
                      ];
                      // Filtros
                      const filterOptions = [
                        { key: null, label: 'Ninguno' },
                        { key: 'company', label: 'Empresa' },
                        { key: 'sede', label: 'Sede' },
                        { key: 'department', label: 'Departamento' },
                        { key: 'area', label: 'Área' },
                        { key: 'section', label: 'Sección' },
                        { key: 'occupation', label: 'Ocupación' }
                      ];
                      const selectedFilter = selectedFiltersForQuestion[0] || null;
                      let barKeys = ['Cantidad'];
                      if (selectedFilter) {
                        // Agrupar por filtro y opción, incluyendo 'Sin responder'
                        const grouped = {};
                        surveyResponses.forEach((resp, idx) => {
                          const filterValue = getFilterValue(resp, selectedFilter);
                          if (!filterValue) return;
                          const answer = responses[idx];
                          if (!grouped[filterValue]) grouped[filterValue] = {};
                          let matched = false;
                          optionLabels.forEach(option => {
                            if (!grouped[filterValue][option]) grouped[filterValue][option] = 0;
                            if (matchesOption(answer, option, optionLabels)) {
                              grouped[filterValue][option]++;
                              matched = true;
                            }
                          });
                          if (!matched) {
                            grouped[filterValue]['Sin responder'] = (grouped[filterValue]['Sin responder'] || 0) + 1;
                          }
                        });
                        data = Object.entries(grouped).map(([filterValue, optionsObj]) => {
                          const entry = { name: filterValue };
                          optionLabels.forEach(option => {
                            entry[option] = optionsObj[option] || 0;
                          });
                          entry['Sin responder'] = optionsObj['Sin responder'] || 0;
                          return entry;
                        });
                        barKeys = [...optionLabels, 'Sin responder'];
                      }
                      return (
                        <div id={getQuestionAnchorId(qKey)} data-analytics-section key={question.id || `${pageIdx}-${qIdx}`} className="analytics-detail-card analytics-question-card" style={{ marginBottom: '30px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <span style={{ fontSize: '1.35rem', fontWeight: 600 }}>{question.title || `Pregunta ${qIdx + 1}`}</span>
                          </div>
                          <QuestionFilterControls
                            selectedFilters={selectedFiltersForQuestion}
                            onToggle={(filterKey) => toggleQuestionFilter(qKey, filterKey)}
                          />
                          <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', minHeight: 360, marginTop: 12 }}>
                            {data.length === 0 ? (
                              <div style={{ textAlign: 'center', color: '#888', fontSize: 18, marginTop: 60 }}>
                                No hay datos para el filtro seleccionado.
                              </div>
                            ) : (
                              <BarChart width={getChartWidth(data)} height={360} data={data} style={{ minWidth: 480 }} barCategoryGap="10%" barSize={getBarSize(data.length, barKeys.length)} margin={{ bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" interval={0} tick={<WrappedTick />} height={60} tickMargin={8} />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={makeTooltip(data, barKeys)} />
                                {barKeys.length === 1 ? (
                                  <Bar dataKey="Cantidad">
                                    {data.map((entry, i) => (
                                      <Cell key={`cell-cb-${i}`} fill={entry.color || COLORS[i % COLORS.length]} />
                                    ))}
                                  </Bar>
                                ) : (
                                  barKeys.map((key, idx) => (
                                    <Bar key={key} dataKey={key} fill={key === 'Sin responder' ? '#e0e0e0' : COLORS[idx % COLORS.length]} />
                                  ))
                                )}
                              </BarChart>
                            )}
                          </div>
                          {/* Leyenda personalizada: centrada y compacta */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', margin: '10px 0 14px' }}>
                            {question.options.map((option, i) => (
                              <div key={option} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ display: 'inline-block', width: 18, height: 18, background: COLORS[i % COLORS.length], borderRadius: 4, border: '1px solid #ccc' }}></span>
                                <span>{option}</span>
                              </div>
                            ))}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 18, height: 18, background: '#e0e0e0', borderRadius: 4, border: '1px solid #ccc' }}></span>
                              <span>Sin responder</span>
                            </div>
                          </div>
                          {/* Tabla resumen de opciones (dinámica por filtro) */}
                          <div style={{ marginTop: 8, padding: '0 8px', maxWidth: '100%', overflowX: 'auto' }}>
                            {selectedFilter ? (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 700 }}>{filterOptions.find(o => o.key === selectedFilter)?.label || 'Filtro'}</th>
                                    {optionLabels.map(opt => (
                                      <th key={`th-cb-${opt}`} style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>{opt}</th>
                                    ))}
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>Sin responder</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>Total</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const grouped = {};
                                    surveyResponses.forEach((resp, idx) => {
                                      const fv = getFilterValue(resp, selectedFilter);
                                      if (!fv) return;
                                      const ans = responses[idx];
                                      if (!grouped[fv]) grouped[fv] = { __na: 0 };
                                      let matchedAny = false;
                                      optionLabels.forEach(opt => {
                                        grouped[fv][opt] = grouped[fv][opt] || 0;
                                        if (matchesOption(ans, opt, optionLabels)) {
                                          grouped[fv][opt] += 1;
                                          matchedAny = true;
                                        }
                                      });
                                      if (!matchedAny) grouped[fv].__na += 1;
                                    });
                                    const entries = Object.entries(grouped);
                                    const rows = entries.map(([fv, obj], i) => {
                                      const na = obj.__na || 0;
                                      const rowTotal = optionLabels.reduce((s, o) => s + (obj[o] || 0), 0) + na;
                                      const pct = surveyResponses.length > 0 ? ((rowTotal / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                      return (
                                        <tr key={`cb-row-${fv}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{fv}</td>
                                          {optionLabels.map(opt => (
                                            <td key={`td-cb-${fv}-${opt}`} style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{obj[opt] || 0}</td>
                                          ))}
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{na}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{rowTotal}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{pct}%</td>
                                        </tr>
                                      );
                                    });
                                    // Totales por columna
                                    const totals = optionLabels.reduce((acc, o) => ({ ...acc, [o]: entries.reduce((s, [, v]) => s + (v[o] || 0), 0) }), {});
                                    const totalNA = entries.reduce((s, [, v]) => s + (v.__na || 0), 0);
                                    const grand = Object.values(totals).reduce((s, n) => s + n, 0) + totalNA;
                                    return [
                                      ...rows,
                                      <tr key="cb-total" style={{ background: '#eef6ff', fontWeight: 800, borderTop: '2px solid #93c5fd' }}>
                                        <td style={{ padding: '12px 14px', color: '#111827' }}>Total</td>
                                        {optionLabels.map(opt => (
                                          <td key={`tot-cb-${opt}`} style={{ textAlign: 'center', padding: '12px 14px' }}>{totals[opt]}</td>
                                        ))}
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>{totalNA}</td>
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>{grand}</td>
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>100.0%</td>
                                      </tr>
                                    ];
                                  })()}
                                </tbody>
                              </table>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>Opción</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>Cantidad</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {question.options.map((option, i) => {
                                    const count = optionCounts[option];
                                    const percent = surveyResponses.length > 0 ? ((count / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                    return (
                                      <tr key={option} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{option}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{count}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{percent}%</td>
                                      </tr>
                                    );
                                  })}
                                  {/* Sin responder */}
                                  <tr style={{ background: question.options.length % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '10px 12px' }}>Sin responder</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{notAnsweredCount}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{surveyResponses.length > 0 ? ((notAnsweredCount / surveyResponses.length) * 100).toFixed(1) : '0.0'}%</td>
                                  </tr>
                                  {/* Total */}
                                  <tr style={{ background: '#f9fafb', fontWeight: 700 }}>
                                    <td style={{ padding: '12px 14px', color: '#111827' }}>Total</td>
                                    <td style={{ textAlign: 'center', padding: '12px 14px' }}>{surveyResponses.length}</td>
                                    <td style={{ textAlign: 'center', padding: '12px 14px' }}>100.0%</td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Si la pregunta es de tipo escala, mostrar gráfico de barras por valor
                    if (question.type === 'scale') {
                      const min = question.min ?? 0;
                      const max = question.max ?? 10;
                      const scaleCounts = {};
                      for (let i = min; i <= max; i++) {
                        scaleCounts[i] = 0;
                      }
                      let notAnsweredCount = 0;
                      responses.forEach(r => {
                        if (typeof r === 'number' && r >= min && r <= max) {
                          scaleCounts[r] = (scaleCounts[r] || 0) + 1;
                        } else {
                          notAnsweredCount++;
                        }
                      });
                      // Paleta de colores
                      const COLORS = ['#FFBB28', '#0088FE', '#00C49F', '#FF8042', '#8884D8', '#82CA9D', '#FF6666', '#A28CFF', '#FFB347', '#B6FFB3'];
                      // Filtros
                      const filterOptions = [
                        { key: null, label: 'Ninguno' },
                        { key: 'company', label: 'Empresa' },
                        { key: 'sede', label: 'Sede' },
                        { key: 'department', label: 'Departamento' },
                        { key: 'area', label: 'Área' },
                        { key: 'section', label: 'Sección' },
                        { key: 'occupation', label: 'Ocupación' }
                      ];
                      const selectedFilter = selectedFiltersForQuestion[0] || null;
                      // Data para el gráfico
                      let data = [
                        ...Array.from({ length: max - min + 1 }, (_, i) => ({
                          name: (min + i).toString(),
                          Cantidad: scaleCounts[min + i],
                          color: COLORS[i % COLORS.length]
                        })),
                        { name: 'Sin responder', Cantidad: notAnsweredCount, color: '#e0e0e0' }
                      ];
                      let barKeys = ['Cantidad'];
                      if (selectedFilter) {
                        // Agrupar por filtro y valor de escala incluyendo 'Sin responder'
                        const grouped = {};
                        surveyResponses.forEach((resp, idx) => {
                          const filterValue = getFilterValue(resp, selectedFilter);
                          if (!filterValue) return;
                          let answer = responses[idx];
                          if (typeof answer === 'string' && /^\d+$/.test(answer.trim())) {
                            answer = Number(answer.trim());
                          }
                          if (!grouped[filterValue]) grouped[filterValue] = {};
                          const key = (typeof answer === 'number' && answer >= min && answer <= max) ? String(answer) : null;
                          if (key !== null) {
                            grouped[filterValue][key] = (grouped[filterValue][key] || 0) + 1;
                          } else {
                            grouped[filterValue]['Sin responder'] = (grouped[filterValue]['Sin responder'] || 0) + 1;
                          }
                        });
                        const valueLabels = Array.from({ length: max - min + 1 }, (_, i) => String(min + i));
                        data = Object.entries(grouped).map(([filterValue, valuesObj]) => {
                          const entry = { name: filterValue };
                          valueLabels.forEach(v => {
                            entry[v] = valuesObj[v] || 0;
                          });
                          entry['Sin responder'] = valuesObj['Sin responder'] || 0;
                          return entry;
                        });
                        barKeys = [...valueLabels, 'Sin responder'];
                      }
                      // Ajustar ancho sólo cuando NO hay filtro (Ninguno) para evitar columnas demasiado separadas
                      const chartWidth = selectedFilter ? getChartWidth(data) : Math.min(getChartWidth(data), 900);
                      return (
                        <div id={getQuestionAnchorId(qKey)} data-analytics-section key={question.id || `${pageIdx}-${qIdx}`} className="analytics-detail-card analytics-question-card" style={{ marginBottom: '30px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <span style={{ fontSize: '1.35rem', fontWeight: 600 }}>{question.title || `Pregunta ${qIdx + 1}`}</span>
                          </div>
                          <QuestionFilterControls
                            selectedFilters={selectedFiltersForQuestion}
                            onToggle={(filterKey) => toggleQuestionFilter(qKey, filterKey)}
                          />
                          <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', minHeight: 360, marginTop: 12 }}>
                            {data.length === 0 ? (
                              <div style={{ textAlign: 'center', color: '#888', fontSize: 18, marginTop: 60 }}>
                                No hay datos para el filtro seleccionado.
                              </div>
                            ) : (
                              <BarChart width={chartWidth} height={360} data={data} style={{ minWidth: 480 }} barCategoryGap="10%" barSize={getBarSize(data.length, barKeys.length)} margin={{ bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" interval={0} tick={<WrappedTick />} height={60} tickMargin={8} />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={makeTooltip(data, barKeys)} />
                                {barKeys.length === 1 ? (
                                  <Bar dataKey="Cantidad">
                                    {data.map((entry, i) => (
                                      <Cell key={`cell-sc-${i}`} fill={entry.color || COLORS[i % COLORS.length]} />
                                    ))}
                                  </Bar>
                                ) : (
                                  barKeys.map((key, idx) => (
                                    <Bar key={key} dataKey={key} fill={key === 'Sin responder' ? '#e0e0e0' : COLORS[idx % COLORS.length]} />
                                  ))
                                )}
                              </BarChart>
                            )}
                          </div>

                          {/* Leyenda personalizada: centrada y compacta */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', margin: '10px 0 12px' }}>
                            {Array.from({ length: max - min + 1 }, (_, i) => (
                              <div key={min + i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ display: 'inline-block', width: 12, height: 12, background: COLORS[i % COLORS.length], borderRadius: 3, border: '1px solid #e5e7eb' }}></span>
                                <span>{min + i}</span>
                              </div>
                            ))}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#e0e0e0', borderRadius: 3, border: '1px solid #e5e7eb' }}></span>
                              <span>Sin responder</span>
                            </div>
                          </div>
                          {/* Tabla resumen de valores (dinámica por filtro con columnas por valor) */}
                          <div style={{ marginTop: 8, padding: '0 8px', maxWidth: '100%', overflowX: 'auto' }}>
                            {selectedFilter ? (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 700 }}>{filterOptions.find(o => o.key === selectedFilter)?.label || 'Filtro'}</th>
                                    {Array.from({ length: max - min + 1 }, (_, i) => (
                                      <th key={`th-scale-${min + i}`} style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>{min + i}</th>
                                    ))}
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>Sin responder</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>Total</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const grouped = {};
                                    surveyResponses.forEach((resp, idx) => {
                                      const fv = getFilterValue(resp, selectedFilter);
                                      if (!fv) return;
                                      let ans = responses[idx];
                                      if (typeof ans === 'string' && /^\d+$/.test(ans?.trim?.() || '')) ans = Number(ans.trim());
                                      if (!grouped[fv]) grouped[fv] = { __na: 0 };
                                      if (typeof ans === 'number' && ans >= min && ans <= max) {
                                        grouped[fv][ans] = (grouped[fv][ans] || 0) + 1;
                                      } else {
                                        grouped[fv].__na += 1;
                                      }
                                    });
                                    const entries = Object.entries(grouped);
                                    const rows = entries.map(([fv, obj], i) => {
                                      const na = obj.__na || 0;
                                      const valueCols = Array.from({ length: max - min + 1 }, (_, k) => obj[min + k] || 0);
                                      const rowTotal = valueCols.reduce((s, n) => s + n, 0) + na;
                                      const pct = surveyResponses.length > 0 ? ((rowTotal / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                      return (
                                        <tr key={`scale-row-${fv}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{fv}</td>
                                          {Array.from({ length: max - min + 1 }, (_, k) => (
                                            <td key={`td-scale-${fv}-${min + k}`} style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{obj[min + k] || 0}</td>
                                          ))}
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{na}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{rowTotal}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{pct}%</td>
                                        </tr>
                                      );
                                    });
                                    // Totales por columna
                                    const totalsByVal = Array.from({ length: max - min + 1 }, (_, k) => entries.reduce((s, [, v]) => s + (v[min + k] || 0), 0));
                                    const totalNA = entries.reduce((s, [, v]) => s + (v.__na || 0), 0);
                                    const grand = totalsByVal.reduce((s, n) => s + n, 0) + totalNA;
                                    return [
                                      ...rows,
                                      <tr key="scale-total" style={{ background: '#eef6ff', fontWeight: 800, borderTop: '2px solid #93c5fd' }}>
                                        <td style={{ padding: '12px 14px', color: '#111827' }}>Total</td>
                                        {Array.from({ length: max - min + 1 }, (_, k) => (
                                          <td key={`tot-scale-${min + k}`} style={{ textAlign: 'center', padding: '12px 14px' }}>{totalsByVal[k]}</td>
                                        ))}
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>{totalNA}</td>
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>{grand}</td>
                                        <td style={{ textAlign: 'center', padding: '12px 14px' }}>100.0%</td>
                                      </tr>
                                    ];
                                  })()}
                                </tbody>
                              </table>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>Valor</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>Cantidad</th>
                                    <th style={{ textAlign: 'center', padding: '12px 14px', fontWeight: 700, letterSpacing: 0.2 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Array.from({ length: max - min + 1 }, (_, i) => {
                                    const val = min + i;
                                    const count = scaleCounts[val];
                                    const percent = surveyResponses.length > 0 ? ((count / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                    return (
                                      <tr key={val} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{val}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{count}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #eef2f7' }}>{percent}%</td>
                                      </tr>
                                    );
                                  })}
                                  {/* Sin responder */}
                                  <tr style={{ background: (max - min + 1) % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '10px 12px' }}>Sin responder</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{notAnsweredCount}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{surveyResponses.length > 0 ? ((notAnsweredCount / surveyResponses.length) * 100).toFixed(1) : '0.0'}%</td>
                                  </tr>
                                  {/* Total */}
                                  <tr style={{ background: '#f9fafb', fontWeight: 700 }}>
                                    <td style={{ padding: '12px 14px' }}>Total</td>
                                    <td style={{ textAlign: 'center', padding: '12px 14px' }}>{surveyResponses.length}</td>
                                    <td style={{ textAlign: 'center', padding: '12px 14px' }}>100.0%</td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Para otros tipos de pregunta, mantener el gráfico anterior
                    if (question.type === 'text' || question.type === 'textarea' || question.type === 'long_text') {

                      const answeredCount = responses.filter(r => isAnswered(r)).length;
                      const notAnsweredCount = surveyResponses.length - answeredCount;
                      const COLORS = ['#0088FE', '#FF8042'];
                      const filterOptions = [
                        { key: null, label: 'Ninguno' },
                        { key: 'company', label: 'Empresa' },
                        { key: 'sede', label: 'Sede' },
                        { key: 'department', label: 'Departamento' },
                        { key: 'area', label: 'Área' },
                        { key: 'section', label: 'Sección' },
                        { key: 'occupation', label: 'Ocupación' }
                      ];
                      const selectedFilter = selectedFiltersForQuestion[0] || null;
                      let data = [
                        { name: 'Respondidas', Cantidad: answeredCount, color: COLORS[0] },
                        { name: 'Sin responder', Cantidad: notAnsweredCount, color: COLORS[1] }
                      ];
                      let barKeys = ['Cantidad'];
                      if (selectedFilter) {
                        const grouped = {};
                        surveyResponses.forEach(resp => {
                          const filterValue = getFilterValue(resp, selectedFilter);
                          if (!filterValue) return;
                          let answer;
                          if (Array.isArray(resp.answers)) {
                            const found = resp.answers.find(a => a.questionId === question.id);
                            if (found && found.answer !== undefined) {
                              answer = found.answer;
                            } else if (resp.answers[pageIdx] && Array.isArray(resp.answers[pageIdx])) {
                              answer = resp.answers[pageIdx][qIdx]?.answer;
                            } else if (resp.answers[qIdx] && resp.answers[qIdx].answer !== undefined) {
                              answer = resp.answers[qIdx].answer;
                            }
                          }
                          const key = isAnswered(answer) ? 'Respondidas' : 'Sin responder';
                          if (!grouped[filterValue]) grouped[filterValue] = { Respondidas: 0, 'Sin responder': 0 };
                          grouped[filterValue][key] += 1;
                        });
                        data = Object.entries(grouped).map(([filterValue, counts]) => ({
                          name: filterValue,
                          Respondidas: counts['Respondidas'] || 0,
                          'Sin responder': counts['Sin responder'] || 0
                        }));
                        barKeys = ['Respondidas', 'Sin responder'];
                      }
                      return (
                        <div id={getQuestionAnchorId(qKey)} data-analytics-section key={question.id || `${pageIdx}-${qIdx}`} className="analytics-detail-card analytics-question-card" style={{ marginBottom: '30px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <span style={{ fontSize: '1.35rem', fontWeight: 600 }}>{question.title || `Pregunta ${qIdx + 1}`}</span>
                          </div>
                          <QuestionFilterControls
                            selectedFilters={selectedFiltersForQuestion}
                            onToggle={(filterKey) => toggleQuestionFilter(qKey, filterKey)}
                          />
                          <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', minHeight: 360 }}>
                            {data.length === 0 ? (
                              <div style={{ textAlign: 'center', color: '#888', fontSize: 18, marginTop: 60 }}>
                                No hay datos para el filtro seleccionado.
                              </div>
                            ) : (
                              <BarChart width={getChartWidth(data)} height={360} data={data} style={{ minWidth: 480 }} barCategoryGap="10%" barSize={getBarSize(data.length, barKeys.length)} margin={{ bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" interval={0} tick={<WrappedTick />} height={60} tickMargin={8} />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={makeTooltip(data, barKeys)} />
                                {barKeys.length === 1 ? (
                                  <Bar dataKey="Cantidad">
                                    {data.map((entry, i) => (
                                      <Cell key={`cell-text-${i}`} fill={entry.color || COLORS[i % COLORS.length]} />
                                    ))}
                                  </Bar>
                                ) : (
                                  <>
                                    <Bar dataKey="Respondidas" fill="#0088FE" />
                                    <Bar dataKey="Sin responder" fill="#FF8042" />
                                  </>
                                )}
                              </BarChart>
                            )}
                          </div>
                          {/* Leyenda de colores para texto */}
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, margin: '10px 0 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#0088FE', borderRadius: 3, border: '1px solid #e5e7eb' }}></span>
                              <span style={{ fontSize: 14 }}>Respondidas</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#FF8042', borderRadius: 3, border: '1px solid #e5e7eb' }}></span>
                              <span style={{ fontSize: 14 }}>Sin responder</span>
                            </div>
                          </div>
                          {/* Tabla resumen para texto */}
                          <div style={{ marginTop: 8, marginLeft: 60, maxWidth: 860 }}>
                            {selectedFilter ? (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 16, borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <thead>
                                  <tr style={{ background: '#f7f7fa', borderBottom: '2px solid #e0e0e0' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}> {filterOptions.find(o => o.key === selectedFilter)?.label || 'Filtro'} </th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>Respondidas</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>Sin responder</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>Total</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const groupedTotals = {};
                                    surveyResponses.forEach((resp, idx) => {
                                      const fv = getFilterValue(resp, selectedFilter);
                                      if (!fv) return;
                                      const ans = responses[idx];
                                      const answered = isAnswered(ans);
                                      if (!groupedTotals[fv]) groupedTotals[fv] = { a: 0, na: 0 };
                                      if (answered) groupedTotals[fv].a += 1; else groupedTotals[fv].na += 1;
                                    });
                                    const rows = Object.entries(groupedTotals).map(([fv, { a, na }], i) => {
                                      const total = a + na;
                                      const pct = surveyResponses.length > 0 ? ((total / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                      return (
                                        <tr key={`text-row-${fv}`} style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f7' }}>
                                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{fv}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{a}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{na}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{total}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{pct}%</td>
                                        </tr>
                                      );
                                    });
                                    const grandA = rows.reduce((s, _, idx) => s + Object.values(groupedTotals)[idx].a, 0);
                                    const grandNA = rows.reduce((s, _, idx) => s + Object.values(groupedTotals)[idx].na, 0);
                                    const grandT = grandA + grandNA;
                                    return [
                                      ...rows,
                                      <tr key="text-total" style={{ background: '#eef6ff', fontWeight: 800, borderTop: '2px solid #93c5fd' }}>
                                        <td style={{ padding: '10px 12px', color: '#111827' }}>Total</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>{grandA}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>{grandNA}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>{grandT}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>100.0%</td>
                                      </tr>
                                    ];
                                  })()}
                                </tbody>
                              </table>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 16, borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <thead>
                                  <tr style={{ background: '#f7f7fa', borderBottom: '2px solid #e0e0e0' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, letterSpacing: 0.2 }}>Resumen</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600, letterSpacing: 0.2 }}>Cantidad</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600, letterSpacing: 0.2 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ background: '#fff' }}>
                                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>Respondidas</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{answeredCount}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{surveyResponses.length > 0 ? ((answeredCount / surveyResponses.length) * 100).toFixed(1) : '0.0'}%</td>
                                  </tr>
                                  <tr style={{ background: '#f5f5f7' }}>
                                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>Sin responder</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{notAnsweredCount}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{surveyResponses.length > 0 ? ((notAnsweredCount / surveyResponses.length) * 100).toFixed(1) : '0.0'}%</td>
                                  </tr>
                                  <tr style={{ background: '#eef6ff', fontWeight: 800, borderTop: '2px solid #93c5fd' }}>
                                    <td style={{ padding: '10px 12px', color: '#111827' }}>Total</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{surveyResponses.length}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>100.0%</td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>

                          {/* Tabla de respuestas individuales */}
                          {/* Tabla de respuestas individuales con paginación */}
                          <div style={{ marginTop: 40, marginLeft: 60, maxWidth: 860 }}>
                            {(() => {
                              const qKey = question.id || `${pageIdx}-${qIdx}`;
                              const pagination = responsesPagination[qKey] || { page: 1, perPage: 10 };
                              const totalResponses = surveyResponses.length;
                              const totalPages = Math.ceil(totalResponses / pagination.perPage);
                              const startIdx = (pagination.page - 1) * pagination.perPage;
                              const endIdx = Math.min(startIdx + pagination.perPage, totalResponses);
                              const paginatedResponses = surveyResponses.slice(startIdx, endIdx);

                              const updatePagination = (updates) => {
                                setResponsesPagination(prev => ({
                                  ...prev,
                                  [qKey]: { ...pagination, ...updates }
                                }));
                              };

                              return (
                                <>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <h4 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0, color: '#374151' }}>
                                      Respuestas Individuales ({totalResponses} total)
                                    </h4>

                                    {/* Selector de cantidad por página */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ color: '#19B394', fontWeight: 500 }}>Filas por página:</span>
                                      <select
                                        value={pagination.perPage}
                                        onChange={(e) => updatePagination({ perPage: parseInt(e.target.value), page: 1 })}
                                        style={{
                                          padding: '6px',
                                          borderRadius: 6,
                                          border: '1px solid #19B394',
                                          fontSize: '16px',
                                          cursor: 'pointer',
                                          minWidth: '60px'
                                        }}
                                      >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={500}>500</option>
                                        <option value={1000}>1000</option>
                                      </select>
                                    </div>
                                  </div>

                                  <div style={{ overflowX: 'auto' }}>
                                    <table style={{
                                      width: '100%',
                                      borderCollapse: 'separate',
                                      borderSpacing: 0,
                                      fontSize: 14,
                                      borderRadius: 8,
                                      overflow: 'hidden',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                    }}>
                                      <thead>
                                        <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                                          <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, minWidth: '50px' }}>
                                            #
                                          </th>
                                          <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, minWidth: '400px' }}>
                                            Respuesta
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {paginatedResponses.map((response, idx) => {
                                          const globalIdx = startIdx + idx;
                                          const answer = responses[globalIdx] || '';

                                          return (
                                            <tr key={`text-response-${globalIdx}`} style={{
                                              background: idx % 2 === 0 ? '#fff' : '#f8f9fa',
                                              borderBottom: '1px solid #e9ecef'
                                            }}>
                                              <td style={{ padding: '10px 12px', fontWeight: 500, color: '#495057' }}>
                                                {globalIdx + 1}
                                              </td>
                                              <td style={{
                                                padding: '10px 12px',
                                                color: '#374151',
                                                wordWrap: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: '1.5'
                                              }}>
                                                {answer || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin respuesta</span>}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Controles de paginación */}
                                  {totalPages > 0 && (
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 8,
                                      marginTop: 16,
                                      padding: '12px 0'
                                    }}>
                                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                                        Mostrando {startIdx + 1}-{endIdx} de {totalResponses} respuestas
                                        {totalPages > 1 && ` • Página ${pagination.page} de ${totalPages}`}
                                      </div>

                                      {totalPages > 0 && (
                                        <div style={{
                                          marginTop: 16,
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          gap: 16
                                        }}>
                                          <button
                                            onClick={() => updatePagination({ page: pagination.page - 1 })}
                                            disabled={pagination.page === 1}
                                            style={{
                                              padding: '8px 16px',
                                              borderRadius: 6,
                                              border: '1px solid #19B394',
                                              background: pagination.page === 1 ? '#eeeeee' : '#fff',
                                              color: pagination.page === 1 ? '#888888' : '#19B394',
                                              cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
                                            }}
                                          >
                                            Anterior
                                          </button>

                                          <span style={{ fontWeight: 500, color: '#19B394' }}>
                                            Página {pagination.page} de {totalPages}
                                          </span>

                                          <button
                                            onClick={() => updatePagination({ page: pagination.page + 1 })}
                                            disabled={pagination.page === totalPages}
                                            style={{
                                              padding: '8px 16px',
                                              borderRadius: 6,
                                              border: '1px solid #19B394',
                                              background: pagination.page === totalPages ? '#eeeeee' : '#fff',
                                              color: pagination.page === totalPages ? '#888888' : '#19B394',
                                              cursor: pagination.page === totalPages ? 'not-allowed' : 'pointer'
                                            }}
                                          >
                                            Siguiente
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      );

                    } else if (question.type === 'slider' || question.type === 'matrix' || question.type === 'file_upload') {
                      // Contar respondidas/sin responder con soporte de filtros
                      const answeredCount = responses.filter(r => isAnswered(r)).length;
                      const notAnsweredCount = surveyResponses.length - answeredCount;
                      const COLORS = ['#0088FE', '#FF8042'];
                      const filterOptions = [
                        { key: null, label: 'Ninguno' },
                        { key: 'company', label: 'Empresa' },
                        { key: 'sede', label: 'Sede' },
                        { key: 'department', label: 'Departamento' },
                        { key: 'area', label: 'Área' },
                        { key: 'section', label: 'Sección' },
                        { key: 'occupation', label: 'Ocupación' }
                      ];
                      const selectedFilter = selectedFiltersForQuestion[0] || null;
                      let data = [
                        { name: 'Respondidas', Cantidad: answeredCount, color: COLORS[0] },
                        { name: 'Sin responder', Cantidad: notAnsweredCount, color: COLORS[1] }
                      ];
                      let barKeys = ['Cantidad'];
                      if (selectedFilter) {
                        const grouped = {};
                        surveyResponses.forEach((resp, idx) => {
                          const filterValue = getFilterValue(resp, selectedFilter);
                          if (!filterValue) return;
                          const answer = responses[idx];
                          const key = isAnswered(answer) ? 'Respondidas' : 'Sin responder';
                          if (!grouped[filterValue]) grouped[filterValue] = { Respondidas: 0, 'Sin responder': 0 };
                          grouped[filterValue][key] += 1;
                        });
                        data = Object.entries(grouped).map(([filterValue, counts]) => ({
                          name: filterValue,
                          Respondidas: counts['Respondidas'] || 0,
                          'Sin responder': counts['Sin responder'] || 0
                        }));
                        barKeys = ['Respondidas', 'Sin responder'];
                      }
                      return (
                        <div id={getQuestionAnchorId(qKey)} data-analytics-section key={question.id || `${pageIdx}-${qIdx}`} className="analytics-detail-card analytics-question-card" style={{ marginBottom: '30px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <span style={{ fontSize: '1.35rem', fontWeight: 600 }}>{question.title || `Pregunta ${qIdx + 1}`}</span>
                          </div>
                          <QuestionFilterControls
                            selectedFilters={selectedFiltersForQuestion}
                            onToggle={(filterKey) => toggleQuestionFilter(qKey, filterKey)}
                          />
                          <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', minHeight: 360 }}>
                            {data.length === 0 ? (
                              <div style={{ textAlign: 'center', color: '#888', fontSize: 18, marginTop: 60 }}>
                                No hay datos para el filtro seleccionado.
                              </div>
                            ) : (
                              <BarChart width={getChartWidth(data)} height={360} data={data} style={{ minWidth: 480 }} barCategoryGap="10%" barSize={getBarSize(data.length, barKeys.length)} margin={{ bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" interval={0} tick={<WrappedTick />} height={60} tickMargin={8} />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={makeTooltip(data, barKeys)} />
                                {barKeys.length === 1 ? (
                                  <Bar dataKey="Cantidad">
                                    {data.map((entry, i) => (
                                      <Cell key={`cell-slf-${i}`} fill={entry.color || COLORS[i % COLORS.length]} />
                                    ))}
                                  </Bar>
                                ) : (
                                  barKeys.map((key, idx) => (
                                    <Bar key={key} dataKey={key} fill={COLORS[idx % COLORS.length]} />
                                  ))
                                )}
                              </BarChart>
                            )}
                          </div>
                          {/* Leyenda de colores para Slider/Matrix/Subida */}
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, margin: '10px 0 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#0088FE', borderRadius: 3, border: '1px solid #e5e7eb' }}></span>
                              <span style={{ fontSize: 14 }}>Respondidas</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#FF8042', borderRadius: 3, border: '1px solid #e5e7eb' }}></span>
                              <span style={{ fontSize: 14 }}>Sin responder</span>
                            </div>
                          </div>
                          {/* Tabla resumen para Slider/Matrix/Subida */}
                          <div style={{ marginTop: 8, marginLeft: 60, maxWidth: 860 }}>
                            {selectedFilter ? (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 16, borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <thead>
                                  <tr style={{ background: '#f7f7fa', borderBottom: '2px solid #e0e0e0' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}> {filterOptions.find(o => o.key === selectedFilter)?.label || 'Filtro'} </th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>Respondidas</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>Sin responder</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>Total</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const groupedTotals = {};
                                    surveyResponses.forEach((resp, idx) => {
                                      const fv = getFilterValue(resp, selectedFilter);
                                      if (!fv) return;
                                      const ans = responses[idx];
                                      const answered = isAnswered(ans);
                                      if (!groupedTotals[fv]) groupedTotals[fv] = { a: 0, na: 0 };
                                      if (answered) groupedTotals[fv].a += 1; else groupedTotals[fv].na += 1;
                                    });
                                    const entries = Object.entries(groupedTotals);
                                    const rows = entries.map(([fv, { a, na }], i) => {
                                      const total = a + na;
                                      const pct = surveyResponses.length > 0 ? ((total / surveyResponses.length) * 100).toFixed(1) : '0.0';
                                      return (
                                        <tr key={`smf-row-${fv}`} style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f7' }}>
                                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{fv}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{a}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{na}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{total}</td>
                                          <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{pct}%</td>
                                        </tr>
                                      );
                                    });
                                    const grandA = entries.reduce((s, [, v]) => s + v.a, 0);
                                    const grandNA = entries.reduce((s, [, v]) => s + v.na, 0);
                                    const grandT = grandA + grandNA;
                                    return [
                                      ...rows,
                                      <tr key="smf-total" style={{ background: '#eef6ff', fontWeight: 800, borderTop: '2px solid #93c5fd' }}>
                                        <td style={{ padding: '10px 12px', color: '#111827' }}>Total</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>{grandA}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>{grandNA}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>{grandT}</td>
                                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>100.0%</td>
                                      </tr>
                                    ];
                                  })()}
                                </tbody>
                              </table>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 16, borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <thead>
                                  <tr style={{ background: '#f7f7fa', borderBottom: '2px solid #e0e0e0' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, letterSpacing: 0.2 }}>Resumen</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600, letterSpacing: 0.2 }}>Cantidad</th>
                                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600, letterSpacing: 0.2 }}>%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ background: '#fff' }}>
                                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>Respondidas</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{answeredCount}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{surveyResponses.length > 0 ? ((answeredCount / surveyResponses.length) * 100).toFixed(1) : '0.0'}%</td>
                                  </tr>
                                  <tr style={{ background: '#f5f5f7' }}>
                                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>Sin responder</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{notAnsweredCount}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{surveyResponses.length > 0 ? ((notAnsweredCount / surveyResponses.length) * 100).toFixed(1) : '0.0'}%</td>
                                  </tr>
                                  <tr style={{ background: '#eef6ff', fontWeight: 800, borderTop: '2px solid #93c5fd' }}>
                                    <td style={{ padding: '10px 12px', color: '#111827' }}>Total</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>{surveyResponses.length}</td>
                                    <td style={{ textAlign: 'center', padding: '10px 12px' }}>100.0%</td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>

                          {/* Tabla de respuestas individuales para preguntas de texto */}
                          <div style={{ marginTop: 20, marginLeft: 60, maxWidth: 860 }}>
                            <h4 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12, color: '#374151' }}>
                              Respuestas Individuales
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{
                                width: '100%',
                                borderCollapse: 'separate',
                                borderSpacing: 0,
                                fontSize: 14,
                                borderRadius: 8,
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                              }}>
                                <thead>
                                  <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, minWidth: '50px' }}>
                                      #
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, minWidth: '120px' }}>
                                      Fecha
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, minWidth: '300px' }}>
                                      Respuesta
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {surveyResponses.map((response, responseIdx) => {
                                    // Debug: mostrar estructura de datos
                                    console.log('Response structure:', response);
                                    console.log('PageIdx:', pageIdx, 'QIdx:', qIdx);
                                    console.log('Response answers:', response.answers);

                                    // Obtener la respuesta usando la misma lógica que funciona en los gráficos
                                    let answer = '';

                                    // Usar directamente el array de responses que ya funciona
                                    const responseForThisQuestion = responses[responseIdx];
                                    answer = responseForThisQuestion || '';

                                    // Si no funciona, usar la lógica mejorada
                                    if (!answer && Array.isArray(response.answers)) {
                                      // Método 1: Buscar por questionId
                                      const foundByQuestionId = response.answers.find(a =>
                                        a && typeof a === 'object' && a.questionId === question.id
                                      );

                                      if (foundByQuestionId) {
                                        answer = foundByQuestionId.answer || '';
                                      } else {
                                        // Método 2: Calcular índice global único
                                        let globalQuestionIndex = 0;
                                        let found = false;

                                        for (let pIdx = 0; pIdx < selectedSurvey.pages.length && !found; pIdx++) {
                                          const currentPage = selectedSurvey.pages[pIdx];
                                          const pageQuestions = currentPage.elements || currentPage.questions || [];

                                          for (let qIdxInner = 0; qIdxInner < pageQuestions.length; qIdxInner++) {
                                            if (pIdx === pageIdx && qIdxInner === qIdx && pageQuestions[qIdxInner].id === question.id) {
                                              if (response.answers[globalQuestionIndex]) {
                                                const resp = response.answers[globalQuestionIndex];
                                                answer = typeof resp === 'object' ? resp.answer : resp;
                                              }
                                              found = true;
                                              break;
                                            }
                                            globalQuestionIndex++;
                                          }
                                        }
                                      }
                                    }

                                    console.log('Final answer:', answer);

                                    return (
                                      <tr key={`text-response-${responseIdx}`} style={{
                                        background: responseIdx % 2 === 0 ? '#fff' : '#f8f9fa',
                                        borderBottom: '1px solid #e9ecef'
                                      }}>
                                        <td style={{ padding: '10px 12px', fontWeight: 500, color: '#495057' }}>
                                          {responseIdx + 1}
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#6c757d', fontSize: '13px' }}>
                                          {response.submittedAt ? new Date(response.submittedAt).toLocaleDateString('es-ES', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          }) : 'N/A'}
                                        </td>
                                        <td style={{
                                          padding: '10px 12px',
                                          color: '#374151',
                                          wordWrap: 'break-word',
                                          whiteSpace: 'pre-wrap',
                                          maxWidth: '400px'
                                        }}>
                                          {answer || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin respuesta</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })
                )}
            </>
          )}

          {surveyResponses.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '45vh', width: '100%' }}>
              <div style={{
                background: '#f5fdfa',
                borderRadius: '18px',
                boxShadow: '0 4px 24px rgba(25,179,148,0.10)',
                padding: '48px 36px',
                maxWidth: 520,
                width: '100%',
                textAlign: 'center',
                margin: '40px 0',
                color: '#19B394',
                fontWeight: 600,
                fontSize: '1.3rem',
                border: '2px solid #19B394',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 18,
                justifyContent: 'center',
                minHeight: 220
              }}>
                <div style={{ fontSize: 54, marginBottom: 8 }}>🗒️</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#19B394', marginBottom: 10 }}>No hay respuestas aún</div>
                <div style={{ color: '#555', fontWeight: 400, fontSize: '1.1rem' }}>
                  Esta encuesta no ha recibido respuestas aún.<br />Comparte el enlace de la encuesta para empezar a recopilar datos.
                </div>
              </div>
            </div>
          )}
          </main>
        </div>
      )}
    </div>
  );
};

export default Analytics;
